import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import * as XLSX from 'xlsx-js-style';
import { adminAPI } from '../../../services/api';
import CMAUserGuideModal from './CMAUserGuideModal';


// Sample window presets for the compute-covariance panel.
// `start(today)` returns the YYYY-MM-DD string to use; `end` is always blank
// (i.e. "up to today"). 'custom' lets the user edit the dates freely.
const yearsAgoISO = (yrs) => {
  const d = new Date();
  d.setFullYear(d.getFullYear() - yrs);
  return d.toISOString().slice(0, 10);
};
const HISTORY_PRESETS = [
  { key: 'full',      label: 'Full sample',         start: () => '',           end: () => '' },
  { key: 'last_10y',  label: 'Last 10 years',       start: () => yearsAgoISO(10), end: () => '' },
  { key: 'last_20y',  label: 'Last 20 years',       start: () => yearsAgoISO(20), end: () => '' },
  { key: 'last_30y',  label: 'Last 30 years',       start: () => yearsAgoISO(30), end: () => '' },
  { key: 'post_gfc',  label: 'Post-GFC (Jul 2009+)',start: () => '2009-07-01', end: () => '' },
  { key: 'post_2000', label: 'Post-2000',           start: () => '2000-01-01', end: () => '' },
  { key: 'post_1990', label: 'Post-1990',           start: () => '1990-01-01', end: () => '' },
  { key: 'custom',    label: 'Custom',              start: null,               end: null },
];


// Percentage input that stores a decimal under the hood (e.g. 0.005 = 0.5%) but
// presents itself as a percentage. Uses a local draft string while focused so the
// user can clear and type freely without `value=0.000` interrupting them; only
// commits on blur / Enter.
const PercentInput = ({ value, onChange, step = 0.01, min = 0, placeholder = '0', decimals = 2, disabled = false }) => {
  const [focused, setFocused] = useState(false);
  const [draft, setDraft] = useState('');
  useEffect(() => {
    if (!focused) {
      // Format only when not actively being edited
      if (value == null || value === 0 || isNaN(value)) setDraft('');
      else setDraft((value * 100).toFixed(decimals));
    }
  }, [value, focused, decimals]);

  const commit = () => {
    const num = parseFloat(draft);
    onChange(isNaN(num) ? 0 : num / 100);
  };

  return (
    <div className="input-group input-group-sm">
      <input
        type="number"
        className="form-control form-control-sm"
        step={step}
        min={min}
        placeholder={placeholder}
        value={draft}
        disabled={disabled}
        onFocus={() => setFocused(true)}
        onChange={e => setDraft(e.target.value)}
        onBlur={() => { commit(); setFocused(false); }}
        onKeyDown={e => { if (e.key === 'Enter') { e.currentTarget.blur(); } }}
      />
      <span className="input-group-text">%</span>
    </div>
  );
};

// Borderless inline percent editor for the Asset Class Data macro block. Stores a
// decimal (0.025) but shows/edits a percentage. A local draft holds the raw string
// while editing so typing intermediate values isn't clobbered by reformatting;
// committing on blur. Renders cleanly in html2canvas exports (no border/spinner).
const MacroCellInput = ({ value, onChange, decimals = 1, step = 0.1 }) => {
  const [draft, setDraft] = useState(null); // null = not editing → show formatted value
  const shown = draft !== null ? draft : (isNaN(value) ? '' : (value * 100).toFixed(decimals));
  return (
    <span style={{ display: 'inline-flex', alignItems: 'baseline', justifyContent: 'flex-end' }}>
      <input
        type="number"
        step={step}
        value={shown}
        onChange={e => setDraft(e.target.value)}
        onBlur={() => {
          if (draft !== null) {
            const n = parseFloat(draft);
            onChange(isNaN(n) ? 0 : n / 100);
          }
          setDraft(null);
        }}
        onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
        style={{
          border: 'none', background: 'transparent', textAlign: 'right', width: '52px',
          fontSize: '0.8rem', color: '#212529', padding: 0, MozAppearance: 'textfield',
        }}
        title="Editable long-run macro assumption"
      />
      <span style={{ fontSize: '0.8rem' }}>%</span>
    </span>
  );
};

const SearchableSelect = ({ value, options, onChange, placeholder = '-- Not mapped --' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [dropUp, setDropUp] = useState(false);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filtered = options.filter(opt => {
    const q = search.toLowerCase();
    return opt.column.toLowerCase().includes(q) ||
      (opt.label && opt.label.toLowerCase().includes(q));
  });

  const selectedOpt = options.find(o => o.column === value);
  const displayText = selectedOpt
    ? (selectedOpt.label ? `${selectedOpt.column} — ${selectedOpt.label}` : selectedOpt.column)
    : '';

  const handleSelect = (val) => {
    onChange(val);
    setIsOpen(false);
    setSearch('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange('');
    setIsOpen(false);
    setSearch('');
  };

  return (
    <div ref={wrapperRef} style={{ position: 'relative' }}>
      <div
        className="form-select form-select-sm d-flex align-items-center justify-content-between"
        style={{ cursor: 'pointer', minHeight: '31px' }}
        onClick={() => {
          if (!isOpen && wrapperRef.current) {
            const rect = wrapperRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            setDropUp(spaceBelow < 270);
          }
          setIsOpen(!isOpen);
          if (!isOpen) setTimeout(() => inputRef.current?.focus(), 0);
        }}
      >
        <span className={value ? '' : 'text-muted'} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {value ? displayText : placeholder}
        </span>
        {value && (
          <span
            onClick={handleClear}
            style={{ marginLeft: '4px', cursor: 'pointer', fontSize: '14px', lineHeight: 1, opacity: 0.6 }}
            title="Clear"
          >
            x
          </span>
        )}
      </div>

      {isOpen && (
        <div
          className="border rounded shadow-sm bg-white"
          style={{
            position: 'absolute',
            ...(dropUp ? { bottom: '100%' } : { top: '100%' }),
            left: 0,
            right: 0,
            zIndex: 1050,
            maxHeight: '250px',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div className="p-1" style={{ borderBottom: '1px solid #dee2e6' }}>
            <input
              ref={inputRef}
              type="text"
              className="form-control form-control-sm"
              placeholder="Search ticker or label..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              onClick={e => e.stopPropagation()}
            />
          </div>
          <div style={{ overflowY: 'auto', maxHeight: '200px' }}>
            <div
              className="dropdown-item text-muted"
              style={{ cursor: 'pointer', padding: '4px 8px', fontSize: '0.875rem' }}
              onClick={() => handleSelect('')}
            >
              {placeholder}
            </div>
            {filtered.map(opt => (
              <div
                key={opt.column}
                className={`dropdown-item${opt.column === value ? ' active' : ''}`}
                style={{ cursor: 'pointer', padding: '4px 8px', fontSize: '0.875rem' }}
                onClick={() => handleSelect(opt.column)}
              >
                <span className="fw-medium">{opt.column}</span>
                {opt.label && (
                  <span className="text-muted ms-2" style={{ fontSize: '0.8rem' }}>
                    {opt.label}
                  </span>
                )}
              </div>
            ))}
            {filtered.length === 0 && (
              <div className="text-muted text-center py-2" style={{ fontSize: '0.875rem' }}>
                No matches
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};


const CapitalMarketAssumptions = () => {
  const [activeTab, setActiveTab] = useState('subAssetClasses');
  const [scenarioInnerTab, setScenarioInnerTab] = useState('returns');
  const [mappings, setMappings] = useState([]);
  const [columns, setColumns] = useState([]);
  const [funds, setFunds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Taxonomy state
  const [broadClasses, setBroadClasses] = useState([]);
  const [subAssetClasses, setSubAssetClasses] = useState([]);
  // {sac_code: {first_date: "YYYY-MM-DD", n_months: int}}
  const [sacHistoryStart, setSacHistoryStart] = useState({});
  const [taxonomyLoaded, setTaxonomyLoaded] = useState(false);

  // Dynamic dropdown options from AP_List / Shadow_Data
  const [sacOptions, setSacOptions] = useState([]);

  // CMA Scenario state
  const [scenarios, setScenarios] = useState([]);
  const [selectedScenarioId, setSelectedScenarioId] = useState(null);
  const [scenarioReturns, setScenarioReturns] = useState([]);
  const [scenarioCovariance, setScenarioCovariance] = useState([]);
  const [covProvenance, setCovProvenance] = useState(null);   // {source, saved_at, ...} how the saved matrix was produced
  const [scenariosLoaded, setScenariosLoaded] = useState(false);
  const [matrixUploading, setMatrixUploading] = useState(false);
  const [matrixResult, setMatrixResult] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newScenarioName, setNewScenarioName] = useState('');
  const [newScenarioDesc, setNewScenarioDesc] = useState('');
  const [cloneFromId, setCloneFromId] = useState(null);
  const [importing, setImporting] = useState(false);
  const [covViewMode, setCovViewMode] = useState('correlation'); // 'correlation' or 'covariance'
  const [showReturnsPaste, setShowReturnsPaste] = useState(false);
  const [returnsPasteText, setReturnsPasteText] = useState('');
  const [returnsPasteError, setReturnsPasteError] = useState(null);
  const [showCovPaste, setShowCovPaste] = useState(false);
  const [covPasteText, setCovPasteText] = useState('');
  const [covPasteError, setCovPasteError] = useState(null);
  const [uploadingScenarioFile, setUploadingScenarioFile] = useState(false);
  const scenarioFileInputRef = useRef(null);

  // Compute-from-history panel state
  const [showCompPanel, setShowCompPanel] = useState(false);
  const [compFrequency, setCompFrequency] = useState('quarterly');
  const [compHistoryPreset, setCompHistoryPreset] = useState('post_1990');
  const [compHistoryStart, setCompHistoryStart] = useState('1990-01-01');
  const [compHistoryEnd, setCompHistoryEnd] = useState('');
  const [compBackfill, setCompBackfill] = useState(false);
  const [compRegimes, setCompRegimes] = useState([]);             // [{regime_name, ...}]
  const [compRegimeWeights, setCompRegimeWeights] = useState({}); // {regime_name: number}
  const [compTilts, setCompTilts] = useState([]);                 // [{a, b, multiplier}]
  const [compLoading, setCompLoading] = useState(false);
  const [compPreview, setCompPreview] = useState(null);           // {asset_classes, correlation, covariance, diagnostics}
  const [compError, setCompError] = useState(null);

  // User guide modal
  const [showGuide, setShowGuide] = useState(false);
  const [guideSection, setGuideSection] = useState('expected_returns');
  const openGuide = (section) => { setGuideSection(section); setShowGuide(true); };

  // --- Asset Class Data tab ---------------------------------------------
  // A presentation table: 10yr return + income + growth (residual) + std dev
  // per selected sub-asset class, taken from the DEFAULT CMA scenario, plus a
  // small editable long-run macro block. Exportable to PNG.
  const acdExportRef = useRef(null);
  const [acdReturns, setAcdReturns] = useState([]);   // default scenario [{sub_asset_class, annual_return, annual_volatility}]
  const [acdLoading, setAcdLoading] = useState(false);
  const [acdExporting, setAcdExporting] = useState(false);
  const [acdDeselected, setAcdDeselected] = useState(() => new Set()); // deselected SAC codes (default: all selected)
  // Long-run macro assumptions are pure assumptions (no data source), so they
  // are edited on the UI and persisted per-browser via localStorage.
  const [acdMacro, setAcdMacro] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('cma_acd_macro') || 'null');
      if (saved && typeof saved === 'object') return { cpi: 0.025, awote: 0.034, franking: 0.73, ...saved };
    } catch { /* ignore corrupt cache */ }
    return { cpi: 0.025, awote: 0.034, franking: 0.73 };
  });
  useEffect(() => {
    localStorage.setItem('cma_acd_macro', JSON.stringify(acdMacro));
  }, [acdMacro]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [columnsRes, mappingsRes, fundsRes] = await Promise.all([
        adminAPI.getCMAColumns(),
        adminAPI.getCMAMappings(),
        adminAPI.getCMAFunds().catch(() => ({ funds: [] })),
      ]);

      setColumns(columnsRes.columns || []);
      setFunds(fundsRes.funds || []);

      // Build mapping state: one entry per sub asset class
      const existingMap = {};
      (mappingsRes.mappings || []).forEach(m => {
        existingMap[m.sub_asset_class] = {
          index_column: m.index_column || '',
          fund_tr_code: m.fund_tr_code || '',
        };
      });

      const allClasses = mappingsRes.sub_asset_classes || [];
      const fullMappings = allClasses.map(ac => ({
        sub_asset_class: ac,
        index_column: existingMap[ac]?.index_column || '',
        fund_tr_code: existingMap[ac]?.fund_tr_code || '',
      }));

      setMappings(fullMappings);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchTaxonomy = useCallback(async () => {
    if (taxonomyLoaded) return;
    try {
      const [data, options, proxiesRes, historyRes] = await Promise.all([
        adminAPI.getTaxonomy(),
        adminAPI.getAssetClassOptions().catch(() => ({ sub_asset_classes: [], asset_class_groups: [] })),
        adminAPI.getCMABackfillProxies().catch(() => ({ proxies: [] })),
        adminAPI.getSacHistoryStart().catch(() => ({ starts: {} })),
      ]);
      setBroadClasses(data.broad_asset_classes || []);
      const proxyMap = {};
      (proxiesRes.proxies || []).forEach(p => {
        proxyMap[p.sub_asset_class] = p.backfill_proxies || [];
      });
      const sacs = (data.sub_asset_classes || []).map(s => ({
        ...s,
        backfill_proxies: proxyMap[s.sub_asset_class] || [],
      }));
      setSubAssetClasses(sacs);
      setSacOptions(options.sub_asset_classes || []);
      setSacHistoryStart(historyRes.starts || {});
      setTaxonomyLoaded(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load taxonomy');
    }
  }, [taxonomyLoaded]);

  // CMA Scenario functions — must be defined before the useEffect that references them
  const fetchScenarios = useCallback(async () => {
    try {
      const data = await adminAPI.getCMAScenarios();
      setScenarios(data.scenarios || []);
      setScenariosLoaded(true);
      // Auto-select the default scenario
      const defaultScenario = (data.scenarios || []).find(s => s.is_default);
      if (defaultScenario && !selectedScenarioId) {
        setSelectedScenarioId(defaultScenario.id);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load scenarios');
    }
  }, [selectedScenarioId]);

  const fetchScenarioReturns = useCallback(async (id) => {
    if (!id) return;
    try {
      const data = await adminAPI.getScenarioReturns(id);
      setScenarioReturns(data.returns || []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load scenario returns');
    }
  }, []);

  const fetchScenarioCovariance = useCallback(async (id) => {
    if (!id) return;
    try {
      const data = await adminAPI.getScenarioCovariance(id);
      setScenarioCovariance(data.covariance || []);
      // Provenance: prefer the structured blob; backfill `source` from
      // correlation_source for scenarios saved before provenance was added.
      let prov = data.provenance ? { ...data.provenance } : null;
      if (prov && !prov.source && data.correlation_source) prov.source = data.correlation_source;
      if (!prov && data.correlation_source) prov = { source: data.correlation_source };
      setCovProvenance(prov);

      // If this scenario's matrix was built from history, prefill the compute
      // panel with the saved periods + assumptions so it reflects how it was made.
      if (prov && prov.source === 'historical') {
        if (prov.frequency) setCompFrequency(prov.frequency);
        setCompBackfill(prov.backfill_method === 'stambaugh');
        const hw = prov.history_window || {};
        const start = hw.start || '';
        const end = hw.end || '';
        setCompHistoryStart(start);
        setCompHistoryEnd(end);
        // Reflect a fixed-date preset where the saved window matches one exactly,
        // otherwise show Custom. (Relative "last N years" presets aren't matched.)
        const fixedPreset = { '': 'full', '2009-07-01': 'post_gfc', '2000-01-01': 'post_2000', '1990-01-01': 'post_1990' };
        setCompHistoryPreset(!end && fixedPreset[start] ? fixedPreset[start] : 'custom');
        setCompRegimeWeights(prov.regime_weights || {});
        setCompTilts(Object.entries(prov.tilts || {}).map(([pair, mult]) => {
          const [a, b] = String(pair).split('|');
          return { a: a || '', b: b || '', multiplier: mult };
        }));
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load scenario covariance');
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'subAssetClasses' || activeTab === 'broadClasses' || activeTab === 'assetClassData') {
      fetchTaxonomy();   // Asset Class Data needs the taxonomy for income yields + display names
    }
    // Scenarios are loaded on first mount AND on demand for the Scenarios tab so
    // the completeness warning banner can render across every tab. The cached
    // `scenariosLoaded` flag prevents redundant fetches.
    if (!scenariosLoaded) {
      fetchScenarios();
    }
  }, [activeTab, fetchTaxonomy, fetchScenarios, scenariosLoaded]);

  // The Asset Class Data table draws its returns/vol from the DEFAULT scenario,
  // independent of whatever scenario is selected on the Scenarios tab.
  const defaultScenarioId = useMemo(() => {
    const d = scenarios.find(s => s.is_default);
    return d ? d.id : null;
  }, [scenarios]);

  useEffect(() => {
    if (activeTab !== 'assetClassData' || !defaultScenarioId) return;
    let cancelled = false;
    setAcdLoading(true);
    adminAPI.getScenarioReturns(defaultScenarioId)
      .then(data => { if (!cancelled) setAcdReturns(data.returns || []); })
      .catch(err => { if (!cancelled) setError(err.response?.data?.detail || 'Failed to load default scenario returns'); })
      .finally(() => { if (!cancelled) setAcdLoading(false); });
    return () => { cancelled = true; };
  }, [activeTab, defaultScenarioId]);

  useEffect(() => {
    if (selectedScenarioId && activeTab === 'scenarios') {
      if (scenarioInnerTab === 'returns') fetchScenarioReturns(selectedScenarioId);
      if (scenarioInnerTab === 'covariance') fetchScenarioCovariance(selectedScenarioId);
    }
  }, [selectedScenarioId, activeTab, scenarioInnerTab, fetchScenarioReturns, fetchScenarioCovariance]);

  const handleCreateScenario = async () => {
    if (!newScenarioName.trim()) return;
    try {
      setSaving(true);
      const data = {
        name: newScenarioName.trim(),
        description: newScenarioDesc.trim() || null,
        clone_from_id: cloneFromId || null,
      };
      const result = await adminAPI.createCMAScenario(data);
      setShowCreateModal(false);
      setNewScenarioName('');
      setNewScenarioDesc('');
      setCloneFromId(null);
      await fetchScenarios();
      setSelectedScenarioId(result.id);
      setSuccess(`Scenario "${result.name}" created`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create scenario');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteScenario = async (id) => {
    if (!window.confirm('Delete this scenario? This cannot be undone.')) return;
    try {
      await adminAPI.deleteCMAScenario(id);
      if (selectedScenarioId === id) setSelectedScenarioId(null);
      await fetchScenarios();
      setSuccess('Scenario deleted');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete scenario');
    }
  };

  const handleSetDefault = async (id) => {
    try {
      await adminAPI.setCMAScenarioDefault(id);
      await fetchScenarios();
      setSuccess('Default scenario updated');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to set default');
    }
  };

  const handleLockScenario = async (id) => {
    if (!window.confirm(
      'Lock (finalise) this scenario?\n\n'
      + 'It becomes read-only — expected returns, covariance and metadata can no '
      + 'longer be changed. Only the scenario owner can unlock it, and doing so '
      + 'requires a reason that is permanently recorded in the lock history.'
    )) return;
    try {
      await adminAPI.lockScenario(id);
      await fetchScenarios();
      setSuccess('Scenario locked (finalised) — now read-only.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to lock scenario');
    }
  };

  // Unlock modal — owner only; a reason is mandatory and permanently logged
  const [unlockTarget, setUnlockTarget] = useState(null);   // scenario row being unlocked
  const [unlockReason, setUnlockReason] = useState('');
  const [unlockHistory, setUnlockHistory] = useState(null); // lock/unlock events for the modal
  const [unlocking, setUnlocking] = useState(false);

  const openUnlockModal = (scenario) => {
    setUnlockTarget(scenario);
    setUnlockReason('');
    setUnlockHistory(null);
    adminAPI.getScenarioLockHistory(scenario.id)
      .then((d) => setUnlockHistory(d.events || []))
      .catch(() => setUnlockHistory([]));
  };

  const handleUnlockScenario = async () => {
    if (!unlockTarget || unlockReason.trim().length < 5) return;
    setUnlocking(true);
    try {
      await adminAPI.unlockScenario(unlockTarget.id, unlockReason.trim());
      setUnlockTarget(null);
      await fetchScenarios();
      setSuccess('Scenario unlocked — it is editable again. The reason has been recorded in the lock history.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to unlock scenario');
    } finally {
      setUnlocking(false);
    }
  };

  // Toggle a scenario's sharing between read/edit (any admin) and read-only (owner only).
  const handleToggleScenarioSharing = async (scenario) => {
    if (!scenario) return;
    const next = scenario.access === 'read' ? 'edit' : 'read';
    try {
      await adminAPI.setCMAScenarioSharing(scenario.id, next);
      await fetchScenarios();
      setSuccess(next === 'read'
        ? 'Scenario is now read-only to other users.'
        : 'Scenario is now editable by all admins.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to change sharing');
    }
  };

  const handleUploadMatrix = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';  // allow re-selecting the same file
    if (!file || !selectedScenarioId) return;
    setMatrixUploading(true);
    setMatrixResult(null);
    setError(null);
    try {
      const result = await adminAPI.uploadCovarianceMatrix(selectedScenarioId, file);
      setMatrixResult(result);
      setSuccess(result.message);
      await fetchScenarioCovariance(selectedScenarioId);
      await fetchScenarioReturns(selectedScenarioId);  // diagonal sets the vols
      await fetchScenarios();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to upload covariance matrix');
    } finally {
      setMatrixUploading(false);
    }
  };

  const handleDownloadMatrixTemplate = async () => {
    if (!selectedScenarioId) return;
    try {
      const res = await adminAPI.downloadCovarianceMatrixTemplate(selectedScenarioId);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `covariance_matrix_${selectedScenario?.name || 'template'}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setError('Failed to download covariance matrix template');
    }
  };

  const handleExportScenario = async (s) => {
    try {
      setError(null);
      const res = await adminAPI.exportCMAScenario(s.id);
      const safe = (s.name || `scenario_${s.id}`).replace(/[^A-Za-z0-9_-]+/g, '_').replace(/^_+|_+$/g, '');
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `cma_scenario_${safe || s.id}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setError('Failed to export scenario');
    }
  };

  const handleCopyScenario = async (sourceScenario) => {
    const proposedName = `${sourceScenario.name} (copy)`;
    const newName = window.prompt(`Copy "${sourceScenario.name}" to a new scenario.\nNew scenario name:`, proposedName);
    if (newName == null) return;
    const trimmed = newName.trim();
    if (!trimmed) { setError('Scenario name cannot be empty'); return; }
    try {
      const result = await adminAPI.createCMAScenario({
        name: trimmed,
        description: sourceScenario.description || null,
        clone_from_id: sourceScenario.id,
      });
      await fetchScenarios();
      setSelectedScenarioId(result.id);
      setSuccess(`Scenario "${result.name}" created from "${sourceScenario.name}"`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to copy scenario');
    }
  };

  const handleFillMissingRows = async (scenario) => {
    const c = scenario.completeness || {};
    const summary = [
      c.missing_returns_count ? `${c.missing_returns_count} SAC return row${c.missing_returns_count === 1 ? '' : 's'}` : null,
      c.missing_pairs_count ? `${c.missing_pairs_count} covariance pair${c.missing_pairs_count === 1 ? '' : 's'}` : null,
    ].filter(Boolean).join(' and ');
    if (!summary) { setSuccess('Scenario already complete'); return; }
    if (!window.confirm(
      `Add placeholder rows for ${summary} to "${scenario.name}"?\n\n` +
      `Returns default to 0% (you can edit them on the Expected Returns tab). ` +
      `Existing rows are not touched.`
    )) return;
    try {
      const result = await adminAPI.fillMissingCMAScenarioRows(scenario.id);
      await fetchScenarios();
      // If we just edited the active scenario's data, refetch the underlying grids too.
      if (selectedScenarioId === scenario.id) {
        if (scenarioInnerTab === 'returns') fetchScenarioReturns(scenario.id);
        if (scenarioInnerTab === 'covariance') fetchScenarioCovariance(scenario.id);
      }
      setSuccess(`Added ${result.returns_added} return row${result.returns_added === 1 ? '' : 's'} and ${result.covariance_added} covariance pair${result.covariance_added === 1 ? '' : 's'}`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fill missing rows');
    }
  };

  const handleRenameScenario = async (scenario) => {
    const newName = window.prompt('Rename scenario:', scenario.name);
    if (newName == null) return;
    const trimmed = newName.trim();
    if (!trimmed) { setError('Scenario name cannot be empty'); return; }
    if (trimmed === scenario.name) return;
    try {
      await adminAPI.updateCMAScenario(scenario.id, { name: trimmed, description: scenario.description });
      await fetchScenarios();
      setSuccess('Scenario renamed');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to rename scenario');
    }
  };

  const handleEditDescription = async (scenario) => {
    if (!scenario) return;
    const next = window.prompt(`Edit description for "${scenario.name}":`, scenario.description || '');
    if (next == null) return;                       // cancelled
    const trimmed = next.trim();
    if (trimmed === (scenario.description || '')) return;  // unchanged
    try {
      await adminAPI.updateCMAScenario(scenario.id, { description: trimmed });
      await fetchScenarios();
      setSuccess('Description updated');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update description');
    }
  };

  const handleImportBlender = async () => {
    if (!window.confirm('Import CMA data from blender DB as a new scenario?')) return;
    try {
      setImporting(true);
      const result = await adminAPI.importBlenderCMA();
      await fetchScenarios();
      setSelectedScenarioId(result.scenario_id);
      setSuccess(`Imported ${result.returns_imported} returns and ${result.covariance_imported} covariance entries as "${result.name}"`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to import from blender');
    } finally {
      setImporting(false);
    }
  };

  const handleDownloadScenarioTemplate = async () => {
    try {
      setError(null);
      const response = await adminAPI.downloadCMAScenarioTemplate();
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'cma_scenario_template.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to download template');
    }
  };

  const handleUploadScenarioExcel = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    try {
      setUploadingScenarioFile(true);
      setError(null);
      const result = await adminAPI.uploadCMAScenarioExcel(file);
      await fetchScenarios();
      setSelectedScenarioId(result.scenario_id);
      setSuccess(
        `Created scenario "${result.name}" with ${result.returns_imported} returns and ${result.covariance_imported} covariance entries` +
          (result.is_default ? ' (set as default)' : '')
      );
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to upload scenario');
    } finally {
      setUploadingScenarioFile(false);
    }
  };

  const handleSaveReturns = async () => {
    if (!selectedScenarioId) return;
    if (scenarioCovariance.length > 0 && !covPsdCheck.valid) {
      setError(`Cannot save: ${covPsdCheck.message}`);
      return;
    }
    try {
      setSaving(true);
      setError(null);
      await adminAPI.saveScenarioReturns(selectedScenarioId, scenarioReturns);
      // Also save covariance — volatility changes recompute covariance in-memory
      if (scenarioCovariance.length > 0) {
        await adminAPI.saveScenarioCovariance(selectedScenarioId, scenarioCovariance);
      }
      setSuccess(`Saved ${scenarioReturns.length} expected returns` +
        (scenarioCovariance.length > 0 ? ` and ${scenarioCovariance.length} covariance entries` : ''));
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save returns');
    } finally {
      setSaving(false);
    }
  };

  // ── Compute from History (Historical covariance pipeline) ─────────────
  const fetchCompRegimes = useCallback(async () => {
    try {
      const data = await adminAPI.getMacroRegimes();
      setCompRegimes(data || []);
    } catch (err) {
      console.error('Failed to load macro regimes', err);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'scenarios' && scenarioInnerTab === 'covariance' && showCompPanel && compRegimes.length === 0) {
      fetchCompRegimes();
    }
  }, [activeTab, scenarioInnerTab, showCompPanel, compRegimes.length, fetchCompRegimes]);

  const buildComputeBody = () => {
    const tiltMap = {};
    compTilts.forEach(t => {
      if (t.a && t.b && t.a !== t.b) {
        tiltMap[`${t.a}|${t.b}`] = parseFloat(t.multiplier) || 1.0;
      }
    });
    const weights = {};
    Object.entries(compRegimeWeights).forEach(([k, v]) => {
      const n = parseFloat(v);
      if (Number.isFinite(n) && n > 0) weights[k] = n;
    });
    return {
      tilts: tiltMap,
      regime_weights: weights,
      history_window: {
        start: compHistoryStart || null,
        end: compHistoryEnd || null,
      },
      backfill_method: compBackfill ? 'stambaugh' : 'none',
      frequency: compFrequency,
    };
  };

  const handleComputePreview = async () => {
    if (!selectedScenarioId) return;
    setCompError(null);
    setCompLoading(true);
    try {
      const body = buildComputeBody();
      const result = await adminAPI.previewScenarioCovariance(selectedScenarioId, body);
      setCompPreview(result);
    } catch (err) {
      setCompError(err.response?.data?.detail || 'Failed to preview covariance');
    } finally {
      setCompLoading(false);
    }
  };

  const handleComputeDiscard = () => {
    setCompPreview(null);
    setCompError(null);
  };

  const handleAddTilt = () => {
    setCompTilts(prev => [...prev, { a: '', b: '', multiplier: 1.5 }]);
  };
  const handleUpdateTilt = (idx, field, value) => {
    setCompTilts(prev => prev.map((t, i) => (i === idx ? { ...t, [field]: value } : t)));
  };
  const handleRemoveTilt = (idx) => {
    setCompTilts(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSaveCovariance = async () => {
    if (!selectedScenarioId) return;
    // Manual/pasted/uploaded grid must be PSD before saving. A computed preview is
    // already PSD-repaired by the pipeline, so it skips this check.
    if (!compPreview && !covPsdCheck.valid) {
      setError(`Cannot save: ${covPsdCheck.message}`);
      return;
    }
    try {
      setSaving(true);
      setError(null);
      if (compPreview) {
        // Persist the previewed (computed-from-history) matrix — re-runs the
        // pipeline with the current settings and writes a reproducibility audit row.
        await adminAPI.computeAndSaveScenarioCovariance(selectedScenarioId, buildComputeBody());
        setCompPreview(null);
        await fetchScenarioCovariance(selectedScenarioId);
        setSuccess('Computed covariance saved to scenario');
      } else {
        await adminAPI.saveScenarioCovariance(selectedScenarioId, scenarioCovariance);
        await fetchScenarioCovariance(selectedScenarioId);
        setSuccess(`Saved ${scenarioCovariance.length} covariance entries`);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save covariance');
    } finally {
      setSaving(false);
    }
  };

  // ── Paste / Download helpers for Expected Returns ──
  const handlePasteReturns = () => {
    setReturnsPasteError(null);
    const delim = returnsPasteText.includes('\t') ? '\t' : ',';
    const lines = returnsPasteText.trim().split('\n').map(l => l.split(delim));
    if (lines.length < 2) {
      setReturnsPasteError('Need at least a header row and one data row.');
      return;
    }
    // Detect header row: look for keywords
    const header = lines[0].map(h => h.trim().toLowerCase());
    let sacCol = header.findIndex(h => h.includes('asset') || h.includes('class') || h.includes('name'));
    let retCol = header.findIndex(h => h.includes('return'));
    let volCol = header.findIndex(h => h.includes('vol'));
    // Fallback: assume columns 0=name, 1=return, 2=vol
    if (sacCol < 0) sacCol = 0;
    if (retCol < 0) retCol = 1;
    if (volCol < 0) volCol = 2;

    const parsed = [];
    for (let i = 1; i < lines.length; i++) {
      const row = lines[i];
      const name = (row[sacCol] || '').trim();
      if (!name) continue;
      const rawRet = (row[retCol] || '').trim();
      const rawVol = (row[volCol] || '').trim();
      let ret = parseFloat(rawRet);
      let vol = parseFloat(rawVol);
      if (isNaN(ret) || isNaN(vol)) {
        setReturnsPasteError(`Row ${i + 1}: could not parse numbers for "${name}".`);
        return;
      }
      // Convert to decimal. An explicit "%" means the cell is in percent units —
      // Excel copies a percentage-formatted cell as text including the sign (e.g.
      // "0.5%"), and parseFloat("0.5%") === 0.5, so always divide by 100 when "%"
      // is present. The old ">1" magnitude heuristic silently mis-read sub-1%
      // values like 0.5% as 50%. Without a "%", keep that heuristic (a bare 7.5
      // is taken as 7.5%, while a bare 0.075 is already decimal).
      ret = rawRet.includes('%') ? ret / 100 : (Math.abs(ret) > 1 ? ret / 100 : ret);
      vol = rawVol.includes('%') ? vol / 100 : (Math.abs(vol) > 1 ? vol / 100 : vol);
      parsed.push({ sub_asset_class: name, annual_return: ret, annual_volatility: vol });
    }
    if (parsed.length === 0) {
      setReturnsPasteError('No data rows found.');
      return;
    }
    // Update scenarioReturns — match by name, update existing or append
    setScenarioReturns(prev => {
      const updated = [...prev];
      parsed.forEach(p => {
        const idx = updated.findIndex(r => r.sub_asset_class === p.sub_asset_class);
        if (idx >= 0) {
          updated[idx] = { ...updated[idx], annual_return: p.annual_return, annual_volatility: p.annual_volatility };
        } else {
          updated.push(p);
        }
      });
      return updated;
    });
    setShowReturnsPaste(false);
    setReturnsPasteText('');
    setSuccess(`Pasted ${parsed.length} expected returns.`);
  };

  const downloadReturnsTemplate = () => {
    // Returns-only template: metadata + expected_returns sheets ONLY (no covariance)
    const wb = XLSX.utils.book_new();
    const metaSheet = XLSX.utils.aoa_to_sheet([
      ['name', 'description', 'is_default'],
      [selectedScenario?.name || '', selectedScenario?.description || '', false],
    ]);
    metaSheet['!cols'] = [{ wch: 30 }, { wch: 50 }, { wch: 12 }];
    XLSX.utils.book_append_sheet(wb, metaSheet, 'metadata');

    const returnsSheet = XLSX.utils.aoa_to_sheet([
      ['sub_asset_class', 'annual_return', 'annual_volatility'],
      ...scenarioReturns.map(r => [
        r.sub_asset_class,
        Number((r.annual_return).toFixed(6)),
        Number((r.annual_volatility).toFixed(6)),
      ]),
    ]);
    returnsSheet['!cols'] = [{ wch: 20 }, { wch: 18 }, { wch: 18 }];
    XLSX.utils.book_append_sheet(wb, returnsSheet, 'expected_returns');

    const fname = `expected_returns_${selectedScenario?.name || 'template'}.xlsx`;
    XLSX.writeFile(wb, fname);
  };

  // ── Paste / Download helpers for Covariance Matrix ──
  const handlePasteCov = () => {
    setCovPasteError(null);
    const delim = covPasteText.includes('\t') ? '\t' : ',';
    const lines = covPasteText.trim().split('\n').map(l => l.split(delim));
    if (lines.length < 2) {
      setCovPasteError('Need at least a header row and one data row.');
      return;
    }
    // First row = header: blank cell + asset class names
    const headerNames = lines[0].slice(1).map(h => h.trim());
    if (headerNames.length === 0) {
      setCovPasteError('Could not find asset class names in the header row.');
      return;
    }
    const n = headerNames.length;
    if (lines.length < n + 1) {
      setCovPasteError(`Expected ${n} data rows but found ${lines.length - 1}.`);
      return;
    }

    // Parse the full NxN matrix
    const matrix = [];
    for (let i = 0; i < n; i++) {
      const row = lines[i + 1];
      const rowName = (row[0] || '').trim();
      if (!rowName) {
        setCovPasteError(`Row ${i + 2}: missing asset class name.`);
        return;
      }
      const vals = [];
      for (let j = 0; j < n; j++) {
        const v = parseFloat(row[j + 1]);
        if (isNaN(v)) {
          setCovPasteError(`Row ${i + 2}, col ${j + 2}: could not parse number.`);
          return;
        }
        vals.push(v);
      }
      matrix.push({ name: rowName, values: vals });
    }

    // Detect if this is correlation (diagonal ~1) or covariance (diagonal >> 1)
    const diagValues = matrix.map((r, i) => r.values[i]);
    const isCorrelation = diagValues.every(d => Math.abs(d - 1.0) < 0.01);

    // Build the vol lookup for covariance recomputation
    const volMap = {};
    scenarioReturns.forEach(r => { volMap[r.sub_asset_class] = r.annual_volatility; });

    // Build covariance entries from lower triangle
    const newCov = [];
    for (let i = 0; i < n; i++) {
      for (let j = i + 1; j < n; j++) {
        const sac1 = matrix[i].name;
        const sac2 = matrix[j].name;
        // Average upper and lower triangle for symmetry
        const raw = (matrix[i].values[j] + matrix[j].values[i]) / 2;
        let corr, cov;
        if (isCorrelation) {
          corr = Math.max(-1, Math.min(1, raw));
          const v1 = volMap[sac1] || 0;
          const v2 = volMap[sac2] || 0;
          cov = corr * v1 * v2;
        } else {
          cov = raw;
          const v1 = volMap[sac1] || 0;
          const v2 = volMap[sac2] || 0;
          corr = (v1 > 0 && v2 > 0) ? cov / (v1 * v2) : 0;
        }
        newCov.push({
          asset_class_1: sac1,
          asset_class_2: sac2,
          correlation: parseFloat(corr.toFixed(6)),
          covariance: parseFloat(cov.toFixed(10)),
        });
      }
    }
    setScenarioCovariance(newCov);
    setShowCovPaste(false);
    setCovPasteText('');
    setSuccess(`Pasted ${isCorrelation ? 'correlation' : 'covariance'} matrix (${n}x${n}, ${newCov.length} pairs).`);
  };

  const downloadCovTemplate = () => {
    // Covariance-only template: metadata + covariance sheets ONLY (no expected returns)
    const wb = XLSX.utils.book_new();
    const metaSheet = XLSX.utils.aoa_to_sheet([
      ['name', 'description', 'is_default'],
      [selectedScenario?.name || '', selectedScenario?.description || '', false],
    ]);
    metaSheet['!cols'] = [{ wch: 30 }, { wch: 50 }, { wch: 12 }];
    XLSX.utils.book_append_sheet(wb, metaSheet, 'metadata');

    // Off-diagonal pairs only — consistent with the bulk-upload covariance sheet format
    const covRows = [];
    for (let i = 0; i < covSacList.length; i++) {
      for (let j = i + 1; j < covSacList.length; j++) {
        const a = covSacList[i];
        const b = covSacList[j];
        const cov = getCovValue(a, b, 'covariance');
        const corr = getCovValue(a, b, 'correlation');
        covRows.push([a, b, Number(cov.toFixed(10)), Number(corr.toFixed(6))]);
      }
    }
    const covSheet = XLSX.utils.aoa_to_sheet([
      ['asset_class_1', 'asset_class_2', 'covariance', 'correlation'],
      ...covRows,
    ]);
    covSheet['!cols'] = [{ wch: 16 }, { wch: 16 }, { wch: 16 }, { wch: 14 }];
    XLSX.utils.book_append_sheet(wb, covSheet, 'covariance');

    const fname = `covariance_${selectedScenario?.name || 'template'}.xlsx`;
    XLSX.writeFile(wb, fname);
  };

  const handleReturnChange = (idx, field, value) => {
    const parsed = parseFloat(value) || 0;
    setScenarioReturns(prev => {
      const updated = prev.map((r, i) => i === idx ? { ...r, [field]: parsed } : r);

      // When volatility changes, recompute all covariance entries involving this asset class
      if (field === 'annual_volatility') {
        const changedSac = prev[idx].sub_asset_class;
        // Build a vol lookup from the updated returns
        const volMap = {};
        updated.forEach(r => { volMap[r.sub_asset_class] = r.annual_volatility; });

        setScenarioCovariance(prevCov => prevCov.map(c => {
          if (c.asset_class_1 === changedSac || c.asset_class_2 === changedSac) {
            const v1 = volMap[c.asset_class_1] || 0;
            const v2 = volMap[c.asset_class_2] || 0;
            return { ...c, covariance: c.correlation * v1 * v2 };
          }
          return c;
        }));
      }

      return updated;
    });
  };

  // Build unique SAC list from returns for covariance matrix rendering
  const covSacList = [...new Set(scenarioReturns.map(r => r.sub_asset_class))].sort();

  // Helpers for the previewed-matrix overlay
  const previewIndexMap = useMemo(() => {
    if (!compPreview?.asset_classes) return null;
    return Object.fromEntries(compPreview.asset_classes.map((ac, i) => [ac, i]));
  }, [compPreview]);

  const getPreviewValue = (ac1, ac2, field) => {
    if (!compPreview || !previewIndexMap) return null;
    const i = previewIndexMap[ac1];
    const j = previewIndexMap[ac2];
    if (i === undefined || j === undefined) return null;
    const matrix = field === 'correlation' ? compPreview.correlation : compPreview.covariance;
    return matrix?.[i]?.[j] ?? null;
  };

  // Build a lookup for the covariance matrix
  const getCovValue = (ac1, ac2, field) => {
    if (ac1 === ac2) {
      if (field === 'correlation') return 1.0;
      // Diagonal covariance = vol^2
      const vol = scenarioReturns.find(r => r.sub_asset_class === ac1)?.annual_volatility || 0;
      return vol * vol;
    }
    // Preview overlay takes precedence when active
    const previewVal = getPreviewValue(ac1, ac2, field);
    if (previewVal !== null && previewVal !== undefined) return previewVal;
    const entry = scenarioCovariance.find(
      c => (c.asset_class_1 === ac1 && c.asset_class_2 === ac2) ||
           (c.asset_class_1 === ac2 && c.asset_class_2 === ac1)
    );
    return entry ? entry[field] : 0;
  };

  const setCovValue = (ac1, ac2, field, value) => {
    const val = parseFloat(value) || 0;
    setScenarioCovariance(prev => {
      const idx = prev.findIndex(
        c => (c.asset_class_1 === ac1 && c.asset_class_2 === ac2) ||
             (c.asset_class_1 === ac2 && c.asset_class_2 === ac1)
      );
      if (idx >= 0) {
        const updated = [...prev];
        updated[idx] = { ...updated[idx], [field]: val };
        // Auto-compute covariance from correlation when editing correlation
        if (field === 'correlation') {
          const vol1 = scenarioReturns.find(r => r.sub_asset_class === ac1)?.annual_volatility || 0;
          const vol2 = scenarioReturns.find(r => r.sub_asset_class === ac2)?.annual_volatility || 0;
          updated[idx].covariance = val * vol1 * vol2;
        }
        return updated;
      }
      // Create new entry
      const vol1 = scenarioReturns.find(r => r.sub_asset_class === ac1)?.annual_volatility || 0;
      const vol2 = scenarioReturns.find(r => r.sub_asset_class === ac2)?.annual_volatility || 0;
      const newEntry = {
        asset_class_1: ac1,
        asset_class_2: ac2,
        covariance: field === 'correlation' ? val * vol1 * vol2 : val,
        correlation: field === 'correlation' ? val : (vol1 > 0 && vol2 > 0 ? val / (vol1 * vol2) : 0),
      };
      return [...prev, newEntry];
    });
  };

  // Nearest positive semi-definite matrix (Higham 2002 alternating projections)
  // Operates on correlation matrix, then reconstructs covariance from vols
  const handleFixPsd = () => {
    if (covSacList.length === 0 || scenarioCovariance.length === 0) return;
    const n = covSacList.length;
    const volMap = {};
    scenarioReturns.forEach(r => { volMap[r.sub_asset_class] = r.annual_volatility; });
    const idxMap = {};
    covSacList.forEach((sac, i) => { idxMap[sac] = i; });

    // Build correlation matrix
    const C = Array.from({ length: n }, () => new Array(n).fill(0));
    for (let i = 0; i < n; i++) C[i][i] = 1.0;
    scenarioCovariance.forEach(c => {
      const i = idxMap[c.asset_class_1];
      const j = idxMap[c.asset_class_2];
      if (i !== undefined && j !== undefined) {
        C[i][j] = c.correlation;
        C[j][i] = c.correlation;
      }
    });

    // Symmetric eigenvalue decomposition via Jacobi iteration
    const eigenDecomp = (A) => {
      const sz = A.length;
      const V = Array.from({ length: sz }, (_, i) => {
        const row = new Array(sz).fill(0); row[i] = 1; return row;
      });
      const M = A.map(r => [...r]);
      for (let iter = 0; iter < 100 * sz; iter++) {
        // Find largest off-diagonal
        let maxVal = 0, p = 0, q = 1;
        for (let i = 0; i < sz; i++)
          for (let j = i + 1; j < sz; j++)
            if (Math.abs(M[i][j]) > maxVal) { maxVal = Math.abs(M[i][j]); p = i; q = j; }
        if (maxVal < 1e-12) break;
        const theta = 0.5 * Math.atan2(2 * M[p][q], M[p][p] - M[q][q]);
        const c = Math.cos(theta), s = Math.sin(theta);
        // Rotate M
        const newM = M.map(r => [...r]);
        for (let i = 0; i < sz; i++) {
          newM[i][p] = c * M[i][p] + s * M[i][q];
          newM[i][q] = -s * M[i][p] + c * M[i][q];
        }
        for (let j = 0; j < sz; j++) {
          M[p][j] = c * newM[p][j] + s * newM[q][j];
          M[q][j] = -s * newM[p][j] + c * newM[q][j];
        }
        // Also apply to off-row/col copies
        for (let i = 0; i < sz; i++) {
          M[i][p] = M[p][i]; M[i][q] = M[q][i];
        }
        // Rotate V
        for (let i = 0; i < sz; i++) {
          const vp = V[i][p], vq = V[i][q];
          V[i][p] = c * vp + s * vq;
          V[i][q] = -s * vp + c * vq;
        }
      }
      return { values: M.map((r, i) => r[i]), vectors: V };
    };

    // Nearest PSD correlation matrix via alternating projections
    let Y = C.map(r => [...r]);
    const dS = Array.from({ length: n }, () => new Array(n).fill(0));
    for (let iter = 0; iter < 200; iter++) {
      // R = Y - dS (Dykstra correction)
      const R = Y.map((r, i) => r.map((v, j) => v - dS[i][j]));
      // Project onto PSD cone: clamp eigenvalues >= 0
      const { values, vectors } = eigenDecomp(R);
      const clamped = values.map(v => Math.max(v, 0));
      // Reconstruct: X = V * diag(clamped) * V^T
      const X = Array.from({ length: n }, () => new Array(n).fill(0));
      for (let i = 0; i < n; i++)
        for (let j = 0; j <= i; j++) {
          let s = 0;
          for (let k = 0; k < n; k++) s += vectors[i][k] * clamped[k] * vectors[j][k];
          X[i][j] = s; X[j][i] = s;
        }
      // dS update
      for (let i = 0; i < n; i++)
        for (let j = 0; j < n; j++)
          dS[i][j] = X[i][j] - R[i][j];
      // Project onto unit diagonal (correlation constraint)
      for (let i = 0; i < n; i++) X[i][i] = 1.0;
      // Symmetrise
      for (let i = 0; i < n; i++)
        for (let j = i + 1; j < n; j++) {
          const avg = (X[i][j] + X[j][i]) / 2;
          X[i][j] = avg; X[j][i] = avg;
        }
      // Check convergence
      let diff = 0;
      for (let i = 0; i < n; i++)
        for (let j = 0; j < n; j++)
          diff += (X[i][j] - Y[i][j]) ** 2;
      Y = X;
      if (Math.sqrt(diff) < 1e-10) break;
    }

    // Update scenarioCovariance with the fixed correlation + recomputed covariance
    const newCov = [];
    for (let i = 0; i < n; i++) {
      for (let j = i + 1; j < n; j++) {
        const sac1 = covSacList[i], sac2 = covSacList[j];
        const corr = Math.max(-1, Math.min(1, Y[i][j]));
        const v1 = volMap[sac1] || 0, v2 = volMap[sac2] || 0;
        newCov.push({
          asset_class_1: sac1,
          asset_class_2: sac2,
          correlation: parseFloat(corr.toFixed(6)),
          covariance: parseFloat((corr * v1 * v2).toFixed(10)),
        });
      }
    }
    setScenarioCovariance(newCov);
    setSuccess('Correlation matrix adjusted to nearest positive definite');
  };

  // Check if covariance matrix is positive semi-definite (via Cholesky decomposition)
  const covPsdCheck = useMemo(() => {
    if (covSacList.length === 0 || scenarioCovariance.length === 0) return { valid: true };
    const n = covSacList.length;
    // Build full covariance matrix
    const matrix = Array.from({ length: n }, () => new Array(n).fill(0));
    const volMap = {};
    scenarioReturns.forEach(r => { volMap[r.sub_asset_class] = r.annual_volatility; });
    const idxMap = {};
    covSacList.forEach((sac, i) => { idxMap[sac] = i; });
    // Diagonal
    covSacList.forEach((sac, i) => {
      const v = volMap[sac] || 0;
      matrix[i][i] = v * v;
    });
    // Off-diagonal
    scenarioCovariance.forEach(c => {
      const i = idxMap[c.asset_class_1];
      const j = idxMap[c.asset_class_2];
      if (i !== undefined && j !== undefined) {
        matrix[i][j] = c.covariance;
        matrix[j][i] = c.covariance;
      }
    });
    // Attempt Cholesky decomposition — fails if not PSD
    const L = Array.from({ length: n }, () => new Array(n).fill(0));
    for (let i = 0; i < n; i++) {
      for (let j = 0; j <= i; j++) {
        let sum = 0;
        for (let k = 0; k < j; k++) sum += L[i][k] * L[j][k];
        if (i === j) {
          const diag = matrix[i][i] - sum;
          if (diag < -1e-10) {
            return { valid: false, message: `Matrix is not positive semi-definite (failed at ${covSacList[i]})` };
          }
          L[i][j] = Math.sqrt(Math.max(0, diag));
        } else {
          L[i][j] = L[j][j] > 1e-15 ? (matrix[i][j] - sum) / L[j][j] : 0;
        }
      }
    }
    return { valid: true };
  }, [covSacList, scenarioCovariance, scenarioReturns]);

  // Selected scenario object
  const selectedScenario = scenarios.find(s => s.id === selectedScenarioId);
  const locked = !!selectedScenario?.is_locked;
  // Sharing: read-only to non-owners. `readOnly` gates all editing (locked OR not
  // editable by the current user); `canManageScenario` (owner) gates sharing+lock.
  const canEditScenario = selectedScenario ? selectedScenario.can_edit !== false : true;
  const canManageScenario = !!selectedScenario?.can_manage;
  const readOnly = locked || !canEditScenario;

  // Scenario selector dropdown component
  const ScenarioSelector = () => (
    <div className="d-flex align-items-center gap-2 mb-3">
      <select
        className="form-select form-select-sm"
        style={{ maxWidth: '300px' }}
        value={selectedScenarioId || ''}
        onChange={e => setSelectedScenarioId(parseInt(e.target.value) || null)}
        title="Active CMA scenario. Returns and covariance edits on these tabs apply only to the selected scenario."
      >
        <option value="">-- Select Scenario --</option>
        {scenarios.map(s => (
          <option key={s.id} value={s.id}>
            {s.name} {s.is_default ? '(Default)' : ''}
          </option>
        ))}
      </select>
      {selectedScenario && !selectedScenario.is_default && (
        <button
          className="btn btn-outline-primary btn-sm"
          onClick={() => handleSetDefault(selectedScenarioId)}
          title="Promote this scenario to be the system default. Any downstream consumer (optimiser, retirement calculator, portfolio lab) that doesn't specify a scenario will fall back to this one."
        >
          Set as Default
        </button>
      )}
      {selectedScenario?.is_default && (
        <span className="badge bg-primary" title="This scenario is the system default — used by any consumer that doesn't specify a scenario explicitly.">Default</span>
      )}
      {selectedScenario && !locked && canEditScenario && (
        <button
          className="btn btn-outline-info btn-sm"
          onClick={() => handleEditDescription(selectedScenario)}
          title="Edit this scenario's description"
        >
          <i className="bi bi-card-text me-1"></i>Edit Description
        </button>
      )}
      {canManageScenario && (
        <button
          className="btn btn-outline-info btn-sm"
          onClick={() => handleToggleScenarioSharing(selectedScenario)}
          title={selectedScenario.access === 'read'
            ? 'This scenario is read-only to other users — click to make it editable by all admins'
            : 'This scenario is editable by all admins — click to make it read-only to others'}
        >
          <i className={`bi ${selectedScenario.access === 'read' ? 'bi-eye-fill' : 'bi-people-fill'} me-1`}></i>
          {selectedScenario.access === 'read' ? 'Read-only' : 'Editable'}
        </button>
      )}
      {canManageScenario && (
        <button
          className="btn btn-outline-dark btn-sm"
          onClick={() => handleLockScenario(selectedScenarioId)}
          title="Finalise this scenario. Once locked it becomes PERMANENTLY read-only — returns, covariance and metadata can no longer be changed, and it cannot be unlocked."
        >
          <i className="bi bi-lock me-1"></i>Lock
        </button>
      )}
      {selectedScenario && !locked && selectedScenario.access === 'read' && !canManageScenario && (
        <span
          className="badge"
          style={{ background: 'var(--info-color)', color: '#fff' }}
          title="Read-only to other users — only the owner can edit or delete."
        >
          <i className="bi bi-eye-fill me-1"></i>Read-only
        </span>
      )}
      {selectedScenario?.is_locked && (
        selectedScenario?.is_owner ? (
          <button
            className="badge bg-dark border-0"
            onClick={() => openUnlockModal(selectedScenario)}
            title={`Finalised${selectedScenario.locked_at ? ' on ' + new Date(selectedScenario.locked_at).toLocaleDateString() : ''} — read-only. Click to unlock (a reason is required and permanently recorded).`}
            style={{ cursor: 'pointer' }}
          >
            <i className="bi bi-lock-fill me-1"></i>Locked
          </button>
        ) : (
          <span
            className="badge bg-dark"
            title={`Finalised${selectedScenario.locked_at ? ' on ' + new Date(selectedScenario.locked_at).toLocaleDateString() : ''} — read-only. Only the owner can unlock it.`}
          >
            <i className="bi bi-lock-fill me-1"></i>Locked
          </span>
        )
      )}
      <button
        className="btn btn-outline-success btn-sm"
        onClick={() => setShowCreateModal(true)}
        title="Create a new empty scenario, optionally cloned from an existing one"
      >
        + New Scenario
      </button>
      {selectedScenario && !selectedScenario.is_default && (
        <button
          className="btn btn-outline-danger btn-sm"
          onClick={() => handleDeleteScenario(selectedScenarioId)}
          title="Delete this scenario. Cannot be undone. The default scenario cannot be deleted."
        >
          Delete
        </button>
      )}
      <button
        className="btn btn-outline-secondary btn-sm"
        onClick={handleImportBlender}
        disabled={importing}
        title="Snapshot the current CMA from the offline Blender DB pipeline as a new scenario. Useful for inheriting whatever the Portfolio Modelling Python script most recently produced."
      >
        {importing ? 'Importing...' : 'Import from Blender DB'}
      </button>
      <button
        className="btn btn-outline-secondary btn-sm"
        onClick={handleDownloadScenarioTemplate}
        title="Download a blank scenario workbook (all 3 sheets: metadata + expected_returns + covariance) for bulk-creating a new scenario via Upload Scenario"
      >
        <i className="bi bi-download me-1"></i>Download Full Scenario Template
      </button>
      <button
        className="btn btn-outline-secondary btn-sm"
        onClick={() => scenarioFileInputRef.current?.click()}
        disabled={uploadingScenarioFile}
        title="Upload a filled template to create a new scenario in one go"
      >
        <i className="bi bi-upload me-1"></i>
        {uploadingScenarioFile ? 'Uploading...' : 'Upload Scenario'}
      </button>
      <input
        ref={scenarioFileInputRef}
        type="file"
        accept=".xlsx,.xlsm"
        style={{ display: 'none' }}
        onChange={handleUploadScenarioExcel}
      />
    </div>
  );

  const handleColumnChange = (subAssetClass, value) => {
    setMappings(prev =>
      prev.map(m =>
        m.sub_asset_class === subAssetClass
          ? { ...m, index_column: value }
          : m
      )
    );
  };

  const handleFundChange = (subAssetClass, value) => {
    setMappings(prev =>
      prev.map(m =>
        m.sub_asset_class === subAssetClass
          ? { ...m, fund_tr_code: value }
          : m
      )
    );
  };

  const getLabelForColumn = (colName) => {
    if (!colName) return null;
    const match = columns.find(c => c.column === colName);
    return match?.label || null;
  };

  const getFundName = (trCode) => {
    if (!trCode) return null;
    const match = funds.find(f => f.tr_code === trCode);
    return match?.fund_name || null;
  };

  // Build fund options in the same shape as column options for SearchableSelect
  const fundOptions = funds.map(f => ({
    column: f.tr_code,
    label: f.fund_name,
  }));

  // Taxonomy handlers
  const handleSacChange = (idx, field, value) => {
    setSubAssetClasses(prev => prev.map((s, i) => i === idx ? { ...s, [field]: value } : s));
  };

  const handleAddSac = () => {
    setSubAssetClasses(prev => [
      ...prev,
      { sub_asset_class: '', sac_name: '', broad_asset_class: (broadClasses[0] && broadClasses[0].name) || '', growth_exposure: 0.0, income_yield: 0.0, income_is_total: false, display_order: prev.length + 1 },
    ]);
  };

  const handleRemoveSac = (idx) => {
    setSubAssetClasses(prev => prev.filter((_, i) => i !== idx));
  };

  const handleBroadChange = (idx, field, value) => {
    setBroadClasses(prev => prev.map((b, i) => i === idx ? { ...b, [field]: value } : b));
  };

  const handleAddBroad = () => {
    setBroadClasses(prev => [
      ...prev,
      { name: '', is_growth: false, display_order: prev.length + 1 },
    ]);
  };

  const handleRemoveBroad = (idx) => {
    setBroadClasses(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSaveTaxonomy = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      const cleanedSacs = subAssetClasses
        .map(s => ({
          sub_asset_class: (s.sub_asset_class || '').trim(),
          sac_name: ((s.sac_name || '').trim()) || null,
          broad_asset_class: (s.broad_asset_class || '').trim(),
          growth_exposure: s.growth_exposure,
          income_yield: s.income_yield,
          display_order: s.display_order,
          is_overlay: !!s.is_overlay,
          mgmt_fee: parseFloat(s.mgmt_fee) || 0,
          income_is_total: !!s.income_is_total,
        }))
        .filter(s => s.sub_asset_class); // drop empty rows
      const result = await adminAPI.saveTaxonomy({
        broad_asset_classes: broadClasses,
        sub_asset_classes: cleanedSacs,
      });
      // Save backfill proxies separately (they aren't part of the taxonomy schema)
      await adminAPI.saveCMABackfillProxies(
        subAssetClasses
          .filter(s => s.sub_asset_class)
          .map(s => ({
            sub_asset_class: s.sub_asset_class,
            backfill_proxies: Array.isArray(s.backfill_proxies) ? s.backfill_proxies : [],
          }))
      );
      setSuccess(result.message);
      // Force the Sub Asset Classes / Broad Classes editors to re-sync from the DB
      // (covers the rename case where the user's local state no longer matches what
      // the server now holds, e.g. an old name was removed).
      setTaxonomyLoaded(false);
      // Taxonomy may now reference SACs that don't exist in any scenario yet — refresh
      // scenario completeness so the warning banner reflects the new state.
      fetchScenarios();
      // The backend cascade-clean drops orphan rows from capital_market_assumptions —
      // refresh the Index Mappings tab's local state so it reflects the DB.
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save taxonomy');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const payload = mappings.map(m => ({
        sub_asset_class: m.sub_asset_class,
        index_column: m.index_column || null,
        fund_tr_code: m.fund_tr_code || null,
      }));

      const result = await adminAPI.saveCMAMappings(payload);

      const mapped = result.mappings.filter(m => m.index_column).length;
      setSuccess(
        `Mappings saved. ${mapped} asset classes mapped. ${result.rows_populated.toLocaleString()} monthly return rows populated.`
      );

      // Update local state with server response
      const updatedMap = {};
      (result.mappings || []).forEach(m => {
        updatedMap[m.sub_asset_class] = {
          index_column: m.index_column || '',
          fund_tr_code: m.fund_tr_code || '',
        };
      });
      setMappings(prev =>
        prev.map(m => ({
          ...m,
          index_column: updatedMap[m.sub_asset_class]?.index_column || '',
          fund_tr_code: updatedMap[m.sub_asset_class]?.fund_tr_code || '',
        }))
      );
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save mappings');
    } finally {
      setSaving(false);
    }
  };

  // Rows for the Asset Class Data table: join the taxonomy (income yield +
  // display name + order) with the default scenario's returns/vol. Growth is the
  // residual (total return − income). Overlays (e.g. CURRENCY) and SACs without a
  // default-scenario return are excluded — no fabricated numbers.
  const acdRows = useMemo(() => {
    const retMap = {};
    acdReturns.forEach(r => { retMap[r.sub_asset_class] = r; });
    return subAssetClasses
      .filter(s => s.sub_asset_class && !s.is_overlay && retMap[s.sub_asset_class])
      .slice()
      .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
      .map(s => {
        const r = retMap[s.sub_asset_class];
        const totalReturn = Number(r.annual_return);
        // Classes ticked "Income = Return" are income-only: income is the
        // default scenario's return and growth is zero, whatever the vintage.
        const income = s.income_is_total ? totalReturn : (Number(s.income_yield) || 0);
        return {
          code: s.sub_asset_class,
          label: s.sac_name || s.sub_asset_class,
          totalReturn,
          income,
          growth: totalReturn - income,
          vol: Number(r.annual_volatility),
        };
      });
  }, [subAssetClasses, acdReturns]);

  const acdSelectedRows = acdRows.filter(r => !acdDeselected.has(r.code));

  const toggleAcdSac = (code) => {
    setAcdDeselected(prev => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code); else next.add(code);
      return next;
    });
  };

  // Export the Asset Class Data + macro tables as a PNG. html2canvas is loaded
  // lazily (same pattern as the Charts tab) so it only ships when someone exports.
  const exportAcdImage = async () => {
    if (!acdExportRef.current) return;
    setAcdExporting(true);
    setError(null);
    try {
      const { default: html2canvas } = await import('html2canvas');
      const canvas = await html2canvas(acdExportRef.current, {
        scale: 2, backgroundColor: '#ffffff', useCORS: true, logging: false,
      });
      const a = document.createElement('a');
      a.href = canvas.toDataURL('image/png');
      a.download = 'asset-class-data.png';
      a.click();
    } catch (e) {
      setError('Image export failed: ' + (e?.message || String(e)));
    } finally {
      setAcdExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="card">
        <div className="card-body text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  const tabs = [
    { key: 'subAssetClasses', label: 'Sub Asset Classes' },
    { key: 'broadClasses', label: 'Broad Asset Classes' },
    { key: 'mappings', label: 'Index Mappings' },
    { key: 'scenarios', label: 'Scenarios' },
    { key: 'assetClassData', label: 'Asset Class Data' },
  ];

  return (
    <>
    <CMAUserGuideModal show={showGuide} onClose={() => setShowGuide(false)} section={guideSection} />
    <div className="card">
      <div className="card-header d-flex justify-content-between align-items-center">
        <h5 className="mb-0">Capital Market Assumptions</h5>
        {activeTab === 'mappings' && (
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Mappings'}
          </button>
        )}
        {(activeTab === 'subAssetClasses' || activeTab === 'broadClasses') && (
          <button className="btn btn-primary" onClick={handleSaveTaxonomy} disabled={saving}>
            {saving ? 'Saving...' : 'Save Taxonomy'}
          </button>
        )}
        {activeTab === 'assetClassData' && (
          <button
            className="btn btn-primary"
            onClick={exportAcdImage}
            disabled={acdExporting || acdSelectedRows.length === 0}
            title="Download the Asset Class Data table as a PNG image"
          >
            <i className="bi bi-image me-1"></i>{acdExporting ? 'Exporting...' : 'Export PNG'}
          </button>
        )}
        {activeTab === 'scenarios' && scenarioInnerTab === 'returns' && selectedScenarioId && (
          <div className="d-flex align-items-center gap-2">
            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={() => openGuide('expected_returns')}
              title="Open the CMA user guide — detailed explanation of how Expected Returns are used downstream"
            >
              <i className="bi bi-question-circle me-1"></i>Help
            </button>
            <button className="btn btn-primary" onClick={handleSaveReturns} disabled={saving || readOnly} title={readOnly ? (locked ? 'Scenario is locked (read-only)' : 'Read-only — only the owner can edit this scenario') : 'Persist the expected returns and the implied covariance to the selected scenario'}>
              {saving ? 'Saving...' : 'Save Returns'}
            </button>
          </div>
        )}
        {activeTab === 'scenarios' && scenarioInnerTab === 'covariance' && selectedScenarioId && (
          <div className="d-flex align-items-center gap-2">
            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={() => openGuide('covariance')}
              title="Open the CMA user guide — detailed explanation of the covariance matrix and the Compute from History pipeline"
            >
              <i className="bi bi-question-circle me-1"></i>Help
            </button>
            {!readOnly && (
              <>
                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={handleDownloadMatrixTemplate}
                  title="Download a square covariance-matrix Excel template (SAC labels on both axes) for this scenario. Values are MONTHLY (annualised ×12 on upload)."
                >
                  <i className="bi bi-download me-1"></i>Matrix Template
                </button>
                <label
                  className={`btn btn-outline-secondary btn-sm mb-0 ${matrixUploading ? 'disabled' : ''}`}
                  title="Upload a square covariance matrix (.pkl or .xlsx). Treated as MONTHLY (annualised ×12); sets each covered SAC's volatility from the diagonal and derives correlations. Partial coverage is accepted."
                >
                  <i className="bi bi-upload me-1"></i>{matrixUploading ? 'Uploading...' : 'Upload Matrix'}
                  <input type="file" accept=".pkl,.xlsx,.xlsm" hidden onChange={handleUploadMatrix} disabled={matrixUploading} />
                </label>
              </>
            )}
            {!covPsdCheck.valid && (
              <>
                <span className="badge bg-danger" title={covPsdCheck.message}>
                  Not Positive Definite
                </span>
                <button
                  className="btn btn-warning btn-sm"
                  onClick={handleFixPsd}
                  disabled={readOnly}
                  title="Run Higham's nearest positive-definite repair on the matrix. Required before saving when PSD is violated."
                >
                  Fix Matrix
                </button>
              </>
            )}
            <button
              className="btn btn-primary"
              onClick={handleSaveCovariance}
              disabled={saving || readOnly}
              title={readOnly
                ? (locked ? 'Scenario is locked (read-only)' : 'Read-only — only the owner can edit this scenario')
                : (compPreview
                    ? 'Save the previewed computed-from-history matrix to this scenario (with a reproducibility audit row)'
                    : 'Save the displayed covariance / correlation matrix (manual / pasted / uploaded) to this scenario')}
            >
              {saving ? 'Saving...' : (compPreview ? 'Save Computed Matrix' : 'Save Covariance')}
            </button>
          </div>
        )}
      </div>
      <div className="card-body">
        {error && (
          <div className="alert alert-danger alert-dismissible fade show">
            {error}
            <button type="button" className="btn-close" onClick={() => setError(null)} />
          </div>
        )}
        {success && (
          <div className="alert alert-success alert-dismissible fade show">
            {success}
            <button type="button" className="btn-close" onClick={() => setSuccess(null)} />
          </div>
        )}

        {activeTab === 'scenarios' && selectedScenarioId && locked && (
          <div className="alert alert-secondary d-flex align-items-center">
            <i className="bi bi-lock-fill me-2"></i>
            <span>
              <strong>{selectedScenario?.name}</strong> is finalised (locked) and read-only.
              {selectedScenario?.locked_at ? ` Locked ${new Date(selectedScenario.locked_at).toLocaleDateString()}.` : ''}
              {selectedScenario?.is_owner
                ? ' Unlock it (reason required, permanently recorded) or clone it to make changes.'
                : ' Only the owner can unlock it; clone it to make changes.'}
            </span>
            {selectedScenario?.is_owner && (
              <button className="btn btn-outline-dark btn-sm ms-auto"
                onClick={() => openUnlockModal(selectedScenario)}>
                <i className="bi bi-unlock me-1"></i>Unlock…
              </button>
            )}
          </div>
        )}
        {activeTab === 'scenarios' && selectedScenarioId && !locked && !canEditScenario && (
          <div className="alert alert-info d-flex align-items-center">
            <i className="bi bi-eye-fill me-2"></i>
            <span>
              <strong>{selectedScenario?.name}</strong> is read-only — only its owner can edit it.
              {' '}Clone it to a new scenario to make changes.
            </span>
          </div>
        )}

        {activeTab === 'scenarios' && scenarioInnerTab === 'covariance' && matrixResult && (
          <div className="alert alert-info alert-dismissible fade show">
            <div><strong>Covariance matrix loaded.</strong> {matrixResult.covered?.length || 0} sub-asset class(es) set (annualised ×{matrixResult.annualisation_factor}).</div>
            {matrixResult.uncovered_scenario_sacs?.length > 0 && (
              <div className="small mt-1">
                <strong>Not covered by the matrix (still need covariance):</strong> {matrixResult.uncovered_scenario_sacs.join(', ')}
              </div>
            )}
            {matrixResult.ignored_matrix_sacs?.length > 0 && (
              <div className="small mt-1 text-muted">
                Ignored (not in this scenario): {matrixResult.ignored_matrix_sacs.join(', ')}
              </div>
            )}
            <button type="button" className="btn-close" onClick={() => setMatrixResult(null)} />
          </div>
        )}

        {/* Covariance provenance — how the currently-saved matrix was produced.
            Persisted on the scenario so loading it shows the periods + assumptions
            (historical), the source file (uploaded), or that it was hand-entered. */}
        {activeTab === 'scenarios' && scenarioInnerTab === 'covariance' && covProvenance?.source && (() => {
          const p = covProvenance;
          const when = p.saved_at ? new Date(p.saved_at).toLocaleString('en-AU') : null;
          const by = p.saved_by ? ` by ${p.saved_by}` : '';
          const meta = {
            manual: { label: 'Manual entry / Excel paste', icon: 'bi-pencil-square' },
            uploaded: { label: 'Uploaded matrix file', icon: 'bi-file-earmark-arrow-up' },
            historical: { label: 'Built from historical data', icon: 'bi-graph-up' },
          };
          const cfg = meta[p.source] || meta.manual;
          const hw = p.history_window || {};
          const period = (hw.start || hw.end)
            ? `${hw.start || 'earliest available'} → ${hw.end || 'latest available'}`
            : 'full available history';
          const tiltCount = p.tilts ? Object.keys(p.tilts).length : 0;
          const regimes = p.regime_weights || {};
          const regimeStr = Object.keys(regimes).length
            ? Object.entries(regimes).map(([k, v]) => `${k} ${(Number(v) * 100).toFixed(0)}%`).join(', ')
            : null;
          return (
            <div className="alert alert-secondary" style={{ fontSize: '0.82rem' }}>
              <div className="d-flex align-items-center mb-1">
                <i className={`bi ${cfg.icon} me-2`}></i>
                <strong>Covariance source: {cfg.label}</strong>
                {when && <span className="text-muted ms-2">— saved {when}{by}</span>}
              </div>
              {p.source === 'historical' && (
                <ul className="mb-0 ps-3">
                  <li><strong>Period:</strong> {period}</li>
                  <li><strong>Frequency:</strong> {p.frequency || 'quarterly'} &nbsp;|&nbsp; <strong>Backfill:</strong> {p.backfill_method || 'none'}</li>
                  {regimeStr && <li><strong>Regime weights:</strong> {regimeStr}</li>}
                  <li><strong>Tilts applied:</strong> {tiltCount > 0 ? `${tiltCount} pair${tiltCount === 1 ? '' : 's'}` : 'none'}</li>
                </ul>
              )}
              {p.source === 'uploaded' && (
                <ul className="mb-0 ps-3">
                  {p.filename && <li><strong>File:</strong> <code>{p.filename}</code></li>}
                  <li><strong>Coverage:</strong> {p.covered_count ?? (p.covered?.length || 0)} sub-asset class(es) {p.annualisation_factor ? `(annualised ×${p.annualisation_factor})` : ''}</li>
                  {p.uncovered_scenario_sacs?.length > 0 && (
                    <li className="text-muted">Not covered by the file: {p.uncovered_scenario_sacs.join(', ')}</li>
                  )}
                </ul>
              )}
              {p.source === 'manual' && (
                <div>Entered/pasted directly into the grid{p.entry_count ? ` — ${p.entry_count} entries` : ''}.</div>
              )}
            </div>
          );
        })()}

        {/* Default-scenario completeness warning — visible across every tab so the
            user can't miss it after adding a new SAC to the taxonomy. */}
        {(() => {
          const defaultScenario = scenarios.find(s => s.is_default);
          const c = defaultScenario?.completeness;
          if (!defaultScenario || !c || c.complete) return null;
          const missingR = c.missing_returns_count || 0;
          const missingP = c.missing_pairs_count || 0;
          const impactedApps = c.impacted_apps || [];
          const impactedPortfolios = c.impacted_portfolios || [];
          const severe = impactedApps.length > 0; // a live portfolio touches a missing SAC
          return (
            <div
              className={`alert ${severe ? 'alert-danger' : 'alert-warning'} d-flex align-items-start justify-content-between`}
              style={{ fontSize: '0.82rem' }}
            >
              <div>
                <strong>
                  <i className={`bi ${severe ? 'bi-exclamation-octagon-fill' : 'bi-exclamation-triangle-fill'} me-2`}></i>
                  Default scenario "{defaultScenario.name}" is incomplete.
                </strong>
                <div className="mt-1">
                  {missingR > 0 && (
                    <div>
                      Missing returns for <code>{(c.missing_returns || []).join(', ')}</code>.
                      {severe && (
                        <> Downstream apps that will be impacted (via active portfolios): <strong>{impactedApps.join(', ')}</strong>.</>
                      )}
                    </div>
                  )}
                  {missingP > 0 && (
                    <div>
                      {missingP} of {c.expected_pairs_count} covariance pair{missingP === 1 ? '' : 's'} missing —
                      undefined pairs silently default to 0.5 correlation downstream (blender / risk model / optimiser).
                    </div>
                  )}
                  {severe && impactedPortfolios.length > 0 && (
                    <div className="text-muted" style={{ fontSize: '0.78rem' }}>
                      Affected portfolios: {impactedPortfolios.join(', ')}
                    </div>
                  )}
                </div>
              </div>
              <div className="ms-3 flex-shrink-0">
                <button
                  className={`btn btn-sm ${severe ? 'btn-light' : 'btn-primary'}`}
                  onClick={() => {
                    setActiveTab('scenarios');
                    setSelectedScenarioId(defaultScenario.id);
                    setScenarioInnerTab(missingR > 0 ? 'returns' : 'covariance');
                  }}
                  title="Jump to the editor for the missing data"
                >
                  Fix now <i className="bi bi-arrow-right-short"></i>
                </button>
              </div>
            </div>
          );
        })()}

        {/* Tab Navigation */}
        <ul className="nav nav-tabs mb-3">
          {tabs.map(t => (
            <li className="nav-item" key={t.key}>
              <button
                className={`nav-link ${activeTab === t.key ? 'active' : ''}`}
                onClick={() => { setActiveTab(t.key); setSuccess(null); }}
                style={{
                  fontSize: '0.82rem',
                  padding: '0.5rem 1rem',
                  ...(activeTab === t.key ? { color: 'var(--primary-color)', borderBottomColor: 'var(--secondary-color)', fontWeight: 600 } : {}),
                }}
              >
                {t.label}
              </button>
            </li>
          ))}
        </ul>

        {/* Index Mappings Tab */}
        {activeTab === 'mappings' && (
          <>
            <p className="text-muted mb-2" style={{ fontSize: '0.8rem' }}>
              Map each sub asset class to a column from the Index Returns Monthly table and an SAA fund from the AP List.
              Saving will populate the monthly historic returns table in the blender database.
            </p>
            <div className="table-responsive">
              <table className="table mb-0">
                <thead>
                  <tr>
                    <th>Sub Asset Class</th>
                    <th>Mapped Ticker</th>
                    <th>Index Column</th>
                    <th>SAA Fund</th>
                    <th>Fund Selector</th>
                  </tr>
                </thead>
                <tbody>
                  {mappings.map(m => {
                    const label = getLabelForColumn(m.index_column);
                    const fundName = getFundName(m.fund_tr_code);
                    return (
                      <tr key={m.sub_asset_class}>
                        <td className="align-middle fw-medium">{m.sub_asset_class}</td>
                        <td className="align-middle">
                          {m.index_column ? (
                            <div>
                              <span className="badge bg-info text-dark">{m.index_column}</span>
                              {label && (
                                <span className="text-muted ms-2" style={{ fontSize: '0.8rem' }}>{label}</span>
                              )}
                            </div>
                          ) : (
                            <span className="text-muted fst-italic">--</span>
                          )}
                        </td>
                        <td>
                          <SearchableSelect
                            value={m.index_column}
                            options={columns}
                            onChange={val => handleColumnChange(m.sub_asset_class, val)}
                          />
                        </td>
                        <td className="align-middle">
                          {m.fund_tr_code ? (
                            <div>
                              <span className="badge bg-info text-dark">{m.fund_tr_code}</span>
                              {fundName && (
                                <div className="text-muted" style={{ fontSize: '0.75rem', marginTop: '2px' }}>{fundName}</div>
                              )}
                            </div>
                          ) : (
                            <span className="text-muted fst-italic">--</span>
                          )}
                        </td>
                        <td>
                          <SearchableSelect
                            value={m.fund_tr_code}
                            options={fundOptions}
                            onChange={val => handleFundChange(m.sub_asset_class, val)}
                            placeholder="-- No fund --"
                          />
                        </td>
                      </tr>
                    );
                  })}
                  {mappings.length === 0 && (
                    <tr>
                      <td colSpan="5" className="text-center text-muted">
                        No sub asset classes found. Run the blender data pipeline first.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* Sub Asset Classes Tab */}
        {activeTab === 'subAssetClasses' && (
          <>
            <p className="text-muted mb-2" style={{ fontSize: '0.8rem' }}>
              Configure the taxonomy for each sub-asset class: broad asset class mapping, growth/defensive classification, and expected income yield.
              Tick Income = Return for income-only classes: their income becomes the default CMA scenario's expected return and growth is zero (Asset Class Data, Blender and the Retirement Calculator; factsheets keep using the income yield).
            </p>
            <div className="table-responsive">
              <table className="table mb-0">
                <thead>
                  <tr>
                    <th>Sub Asset Class</th>
                    <th title="Optional client-friendly display name used by apps that prefer a readable label over the canonical SAC code. Blank = fall back to the code.">Client Name</th>
                    <th>Broad Asset Class</th>
                    <th>Classification</th>
                    <th title="Mark as a notional FX-style overlay (e.g. CURRENCY). Overlays are excluded from the optimiser's sum-to-1 constraint.">Overlay</th>
                    <th>Income Yield</th>
                    <th title="Tick to treat the class as income-only: income = the default CMA scenario's expected total return, growth = 0. Used by the Asset Class Data tab, Blender and the Retirement Calculator (not factsheets). The income yield is kept but ignored while ticked.">Income = Return</th>
                    <th title="Implementation fee (annual). Used by the optimiser's fee_budget constraint to enforce a weighted-average fee cap.">Fee</th>
                    <th>Order</th>
                    <th title="Earliest date this SAC has data in blender.monthly_historic_returns. The shortest series caps the rectangular panel used by Ledoit-Wolf, so this is the single most useful number for diagnosing why a longer history window doesn't change the covariance matrix.">Data Start</th>
                    <th title="SAC codes used as OLS regressors to backfill this asset's pre-history months (Stambaugh-style). Comma-separated.">Backfill Proxies</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {subAssetClasses.map((s, idx) => (
                    <tr key={idx}>
                      <td>
                        <input
                          type="text"
                          list={`sac-options-${idx}`}
                          className="form-control form-control-sm"
                          placeholder="Type or pick a code"
                          value={s.sub_asset_class}
                          onChange={e => handleSacChange(idx, 'sub_asset_class', e.target.value)}
                          title="Type a new sub-asset class code (e.g. EQ_JAPAN) or pick from the suggestion list. Whitespace is trimmed on save."
                        />
                        <datalist id={`sac-options-${idx}`}>
                          {sacOptions.map(sac => (
                            <option key={sac} value={sac}>{sac}</option>
                          ))}
                        </datalist>
                      </td>
                      <td>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="e.g. Australian Equities"
                          value={s.sac_name || ''}
                          onChange={e => handleSacChange(idx, 'sac_name', e.target.value)}
                          title="Optional. Used by apps that show a friendly label instead of the SAC code."
                        />
                      </td>
                      <td>
                        <select
                          className="form-select form-select-sm"
                          value={s.broad_asset_class || ''}
                          onChange={e => handleSacChange(idx, 'broad_asset_class', e.target.value)}
                        >
                          <option value="">-- Select --</option>
                          {broadClasses.map(b => (
                            <option key={b.name} value={b.name}>{b.name}</option>
                          ))}
                          {s.broad_asset_class && !broadClasses.some(b => b.name === s.broad_asset_class) && (
                            <option value={s.broad_asset_class}>{s.broad_asset_class} (not in taxonomy)</option>
                          )}
                        </select>
                      </td>
                      <td>
                        <select
                          className="form-select form-select-sm"
                          value={s.growth_exposure >= 1.0 ? '1' : '0'}
                          onChange={e => handleSacChange(idx, 'growth_exposure', parseFloat(e.target.value))}
                          disabled={!!s.is_overlay}
                          title={s.is_overlay ? 'Overlays bypass the growth/defensive classification' : ''}
                        >
                          <option value="1">Growth</option>
                          <option value="0">Defensive</option>
                        </select>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={!!s.is_overlay}
                          onChange={e => handleSacChange(idx, 'is_overlay', e.target.checked)}
                          title="Tick to treat this SAC as a notional FX-style overlay (excluded from the optimiser's sum-to-1 constraint)"
                        />
                      </td>
                      <td>
                        <PercentInput
                          value={s.income_yield}
                          onChange={v => handleSacChange(idx, 'income_yield', v)}
                          step={0.01}
                          decimals={2}
                          disabled={!!s.income_is_total}
                        />
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={!!s.income_is_total}
                          onChange={e => handleSacChange(idx, 'income_is_total', e.target.checked)}
                          title="Income = default scenario total return; growth = 0. The income yield is kept but ignored while ticked."
                        />
                      </td>
                      <td>
                        <PercentInput
                          value={s.mgmt_fee}
                          onChange={v => handleSacChange(idx, 'mgmt_fee', v)}
                          step={0.01}
                          decimals={2}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          value={s.display_order}
                          onChange={e => handleSacChange(idx, 'display_order', parseInt(e.target.value) || 0)}
                        />
                      </td>
                      <td style={{ whiteSpace: 'nowrap', fontFamily: 'monospace', fontSize: '0.78rem' }}>
                        {(() => {
                          const h = sacHistoryStart[s.sub_asset_class];
                          if (!h) {
                            return <span className="text-muted" title="No data found in monthly_historic_returns for this SAC">—</span>;
                          }
                          return (
                            <span title={`${h.n_months} months of data`}>
                              {h.first_date}
                              <small className="text-muted ms-1">({h.n_months}m)</small>
                            </span>
                          );
                        })()}
                      </td>
                      <td style={{ minWidth: '180px' }}>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="e.g. EQ_AU, EQ_WORLD"
                          value={(s.backfill_proxies || []).join(', ')}
                          onChange={e => {
                            const codes = e.target.value
                              .split(',')
                              .map(c => c.trim())
                              .filter(Boolean);
                            handleSacChange(idx, 'backfill_proxies', codes);
                          }}
                        />
                      </td>
                      <td>
                        <button
                          className="btn btn-outline-danger btn-sm"
                          onClick={() => handleRemoveSac(idx)}
                          title="Remove"
                        >
                          &times;
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button className="btn btn-outline-primary btn-sm" onClick={handleAddSac}>
              + Add Sub Asset Class
            </button>
          </>
        )}

        {/* Broad Asset Classes Tab */}
        {activeTab === 'broadClasses' && (
          <>
            <p className="text-muted mb-2" style={{ fontSize: '0.8rem' }}>
              Broad asset class categories used for portfolio lookthrough and reporting.
            </p>
            <div className="table-responsive">
              <table className="table mb-0">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Growth Class</th>
                    <th>Order</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {broadClasses.map((b, idx) => (
                    <tr key={idx}>
                      <td>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={b.name}
                          onChange={e => handleBroadChange(idx, 'name', e.target.value)}
                        />
                      </td>
                      <td>
                        <div className="form-check form-switch mt-1">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={b.is_growth}
                            onChange={e => handleBroadChange(idx, 'is_growth', e.target.checked)}
                          />
                          <label className="form-check-label" style={{ fontSize: '0.85rem' }}>
                            {b.is_growth ? 'Growth' : 'Defensive'}
                          </label>
                        </div>
                      </td>
                      <td>
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          value={b.display_order}
                          onChange={e => handleBroadChange(idx, 'display_order', parseInt(e.target.value) || 0)}
                        />
                      </td>
                      <td>
                        <button
                          className="btn btn-outline-danger btn-sm"
                          onClick={() => handleRemoveBroad(idx)}
                          title="Remove"
                        >
                          &times;
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button className="btn btn-outline-primary btn-sm" onClick={handleAddBroad}>
              + Add Broad Asset Class
            </button>
          </>
        )}

        {/* Scenarios Tab */}
        {activeTab === 'scenarios' && (
          <>
            <p className="text-muted mb-2" style={{ fontSize: '0.8rem' }}>
              CMA scenarios store a set of expected returns, volatilities, and covariances. The default scenario
              is what the Blender, Risk Model, and SOA Builder load when no scenario is specified.
            </p>

            <div className="d-flex gap-2 mb-3">
              <button
                className="btn btn-sm btn-primary"
                onClick={() => { setShowCreateModal(true); setNewScenarioName(''); setNewScenarioDesc(''); setCloneFromId(null); }}
              >
                <i className="bi bi-plus-circle me-1"></i>New Scenario
              </button>
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={handleImportBlender}
                disabled={importing}
                title="Pull the legacy blender DB ac_expected_returns / ac_covariance into a new scenario"
              >
                <i className="bi bi-download me-1"></i>
                {importing ? 'Importing...' : 'Import from Blender DB'}
              </button>
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={handleDownloadScenarioTemplate}
                title="Download an Excel template you can fill out and upload to create a new scenario"
              >
                <i className="bi bi-file-earmark-excel me-1"></i>Excel Template
              </button>
              <button
                className="btn btn-sm btn-outline-primary"
                onClick={() => scenarioFileInputRef.current?.click()}
                title="Upload a filled-out Excel template to create a new scenario"
              >
                <i className="bi bi-upload me-1"></i>Upload Excel
              </button>
              <input
                ref={scenarioFileInputRef}
                type="file"
                accept=".xlsx,.xlsm"
                style={{ display: 'none' }}
                onChange={handleUploadScenarioExcel}
              />
            </div>

            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: '0.78rem' }}>
                <thead>
                  <tr>
                    {['Name', 'Description', 'Default', 'Completeness', 'Created', 'Actions'].map((h, i, arr) => (
                      <th key={h} style={{
                        background: 'var(--primary-color)', color: '#fff', fontWeight: 600,
                        padding: '6px 10px', textAlign: i === arr.length - 1 ? 'right' : 'left',
                        whiteSpace: 'nowrap', borderBottom: '2px solid #002244',
                      }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {scenarios.length === 0 && (
                    <tr>
                      <td colSpan={6} style={{ padding: '1.5rem', textAlign: 'center', color: '#6c757d' }}>
                        No scenarios yet. Create one or import from the legacy blender DB.
                      </td>
                    </tr>
                  )}
                  {scenarios.map((s) => {
                    const isSelected = s.id === selectedScenarioId;
                    return (
                      <tr
                        key={s.id}
                        style={{ cursor: 'pointer', background: isSelected ? 'rgba(89,155,227,0.08)' : 'transparent' }}
                        onClick={() => setSelectedScenarioId(s.id)}
                      >
                        <td style={{ padding: '6px 10px', borderBottom: '1px solid #f0f0f0', fontWeight: 600, color: 'var(--primary-color)' }}>
                          {s.name}
                        </td>
                        <td style={{ padding: '6px 10px', borderBottom: '1px solid #f0f0f0', color: '#6c757d' }}>
                          {s.description || <span style={{ color: '#adb5bd' }}>—</span>}
                        </td>
                        <td style={{ padding: '6px 10px', borderBottom: '1px solid #f0f0f0' }}>
                          {s.is_default ? (
                            <span style={{
                              display: 'inline-block', padding: '2px 8px', borderRadius: 3,
                              background: 'var(--secondary-color)', color: 'var(--primary-color)',
                              fontSize: '0.7rem', fontWeight: 700,
                            }}>DEFAULT</span>
                          ) : (
                            <span style={{ color: '#adb5bd' }}>—</span>
                          )}
                        </td>
                        <td style={{ padding: '6px 10px', borderBottom: '1px solid #f0f0f0' }}>
                          {(() => {
                            const c = s.completeness;
                            if (!c) return <span style={{ color: '#adb5bd' }}>—</span>;
                            const missingR = c.missing_returns_count || 0;
                            const missingP = c.missing_pairs_count || 0;
                            const isComplete = !!c.complete;
                            const parts = [];
                            if (missingR) parts.push(`${missingR} SAC return${missingR === 1 ? '' : 's'} missing`);
                            if (missingP) parts.push(`${missingP} covariance pair${missingP === 1 ? '' : 's'} missing`);
                            const headline = isComplete ? 'Complete' : parts.join(' · ');

                            const tip = isComplete
                              ? 'All taxonomy SACs have returns and covariance pairs in this scenario.'
                              : [
                                  c.missing_returns && c.missing_returns.length
                                    ? `Missing returns for: ${c.missing_returns.join(', ')}`
                                    : null,
                                  missingP ? `${missingP} of ${c.expected_pairs_count} covariance pairs missing — undefined pairs silently default to 0.5 correlation downstream.` : null,
                                  s.is_default && c.impacted_apps && c.impacted_apps.length
                                    ? `Apps using missing SACs (via active portfolios): ${c.impacted_apps.join(', ')}`
                                    : null,
                                  s.is_default && c.impacted_portfolios && c.impacted_portfolios.length
                                    ? `Affected portfolios: ${c.impacted_portfolios.join(', ')}`
                                    : null,
                                ].filter(Boolean).join('\n');

                            const bg = isComplete
                              ? 'var(--success-color)'
                              : (s.is_default && (c.impacted_apps || []).length ? '#dc3545' : 'var(--warning-color)');
                            const fg = isComplete ? 'var(--primary-color)' : (bg === '#dc3545' ? '#fff' : 'var(--primary-color)');

                            return (
                              <span title={tip} style={{
                                display: 'inline-block', padding: '2px 8px', borderRadius: 3,
                                background: bg, color: fg,
                                fontSize: '0.7rem', fontWeight: 700, cursor: 'help',
                              }}>
                                {headline}
                              </span>
                            );
                          })()}
                        </td>
                        <td style={{ padding: '6px 10px', borderBottom: '1px solid #f0f0f0', color: '#6c757d' }}>
                          {s.created_at ? new Date(s.created_at).toLocaleDateString('en-AU') : '—'}
                        </td>
                        <td style={{ padding: '6px 10px', borderBottom: '1px solid #f0f0f0', textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                          {!s.is_default && (
                            <button
                              className="btn btn-sm p-0 me-3"
                              onClick={() => handleSetDefault(s.id)}
                              title="Set as default — Blender / Risk Model / SOA Builder will use this scenario"
                              style={{ color: 'var(--secondary-color)', border: 'none', background: 'none', fontSize: '0.85rem' }}
                            >
                              <i className="bi bi-star"></i>
                            </button>
                          )}
                          {s.completeness && !s.completeness.complete && s.can_edit !== false && (
                            <button
                              className="btn btn-sm p-0 me-3"
                              onClick={() => handleFillMissingRows(s)}
                              title={`Add placeholder rows for the ${s.completeness.missing_returns_count} missing SAC return row(s) and ${s.completeness.missing_pairs_count} missing covariance pair(s). Existing rows are not touched.`}
                              style={{ color: 'var(--warning-color)', border: 'none', background: 'none', fontSize: '0.85rem' }}
                            >
                              <i className="bi bi-plus-square"></i>
                            </button>
                          )}
                          {s.can_edit !== false && (
                            <button
                              className="btn btn-sm p-0 me-3"
                              onClick={() => handleRenameScenario(s)}
                              title="Rename"
                              style={{ color: 'var(--info-color)', border: 'none', background: 'none', fontSize: '0.85rem' }}
                            >
                              <i className="bi bi-pencil-square"></i>
                            </button>
                          )}
                          {s.can_edit !== false && (
                            <button
                              className="btn btn-sm p-0 me-3"
                              onClick={() => handleEditDescription(s)}
                              title="Edit description"
                              style={{ color: 'var(--info-color)', border: 'none', background: 'none', fontSize: '0.85rem' }}
                            >
                              <i className="bi bi-card-text"></i>
                            </button>
                          )}
                          <button
                            className="btn btn-sm p-0 me-3"
                            onClick={() => handleExportScenario(s)}
                            title="Export to Excel — full detail: returns, covariance pairs, square correlation & covariance matrices, and build provenance"
                            style={{ color: 'var(--success-color)', border: 'none', background: 'none', fontSize: '0.85rem' }}
                          >
                            <i className="bi bi-file-earmark-excel"></i>
                          </button>
                          <button
                            className="btn btn-sm p-0 me-3"
                            onClick={() => handleCopyScenario(s)}
                            title="Copy — creates a new scenario with the same returns and covariance"
                            style={{ color: 'var(--info-color)', border: 'none', background: 'none', fontSize: '0.85rem' }}
                          >
                            <i className="bi bi-files"></i>
                          </button>
                          {s.can_manage && (
                            <button
                              className="btn btn-sm p-0 me-3"
                              onClick={() => handleToggleScenarioSharing(s)}
                              title={s.access === 'read'
                                ? 'Read-only to others — click to make editable by all admins'
                                : 'Editable by all admins — click to make read-only to others'}
                              style={{ color: 'var(--info-color)', border: 'none', background: 'none', fontSize: '0.85rem' }}
                            >
                              <i className={`bi ${s.access === 'read' ? 'bi-eye-fill' : 'bi-people-fill'}`}></i>
                            </button>
                          )}
                          {!s.is_locked && s.access === 'read' && !s.can_manage && (
                            <span
                              className="me-3"
                              title="Read-only to other users — only the owner can edit or delete."
                              style={{ color: 'var(--info-color)', fontSize: '0.85rem', cursor: 'help' }}
                            >
                              <i className="bi bi-eye-fill"></i>
                            </span>
                          )}
                          {s.is_locked ? (
                            s.is_owner ? (
                              <button
                                className="btn btn-sm p-0 me-3"
                                onClick={() => openUnlockModal(s)}
                                title={`Finalised${s.locked_at ? ' on ' + new Date(s.locked_at).toLocaleDateString('en-AU') : ''} — read-only. Click to unlock (a reason is required and permanently recorded).`}
                                style={{ color: 'var(--dark-color)', border: 'none', background: 'none', fontSize: '0.85rem' }}
                              >
                                <i className="bi bi-lock-fill"></i>
                              </button>
                            ) : (
                              <span
                                className="me-3"
                                title={`Finalised${s.locked_at ? ' on ' + new Date(s.locked_at).toLocaleDateString('en-AU') : ''} — read-only. Only the owner can unlock it; clone it to make changes.`}
                                style={{ color: 'var(--dark-color)', fontSize: '0.85rem', cursor: 'help' }}
                              >
                                <i className="bi bi-lock-fill"></i>
                              </span>
                            )
                          ) : s.can_manage ? (
                            <button
                              className="btn btn-sm p-0 me-3"
                              onClick={() => handleLockScenario(s.id)}
                              title="Lock (finalise) — makes this scenario read-only. Only the owner can unlock it, with a reason that is permanently recorded."
                              style={{ color: 'var(--dark-color)', border: 'none', background: 'none', fontSize: '0.85rem' }}
                            >
                              <i className="bi bi-unlock"></i>
                            </button>
                          ) : null}
                          {(() => {
                            const noDelete = s.is_default || s.can_edit === false;
                            const delTitle = s.is_locked ? 'Locked — cannot be deleted'
                              : s.can_edit === false ? 'Read-only — only the owner can delete'
                              : s.is_default ? 'Set another scenario as default before deleting this one'
                              : 'Delete';
                            return (
                              <button
                                className="btn btn-sm p-0"
                                onClick={() => !noDelete && handleDeleteScenario(s.id)}
                                disabled={noDelete}
                                title={delTitle}
                                style={{
                                  color: noDelete ? '#adb5bd' : '#dc3545',
                                  border: 'none', background: 'none', fontSize: '0.85rem',
                                  cursor: noDelete ? 'not-allowed' : 'pointer',
                                }}
                              >
                                <i className="bi bi-trash"></i>
                              </button>
                            );
                          })()}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* Inner sub-tab strip for the selected scenario */}
        {activeTab === 'scenarios' && selectedScenarioId && (
          <ul className="nav nav-tabs mt-4 mb-3">
            {[
              { key: 'returns', label: 'Expected Returns' },
              { key: 'covariance', label: 'Covariance Matrix' },
            ].map((t) => (
              <li className="nav-item" key={t.key}>
                <button
                  className={`nav-link ${scenarioInnerTab === t.key ? 'active' : ''}`}
                  onClick={() => { setScenarioInnerTab(t.key); setSuccess(null); }}
                  style={{
                    fontSize: '0.82rem',
                    padding: '0.5rem 1rem',
                    ...(scenarioInnerTab === t.key ? { color: 'var(--primary-color)', borderBottomColor: 'var(--secondary-color)', fontWeight: 600 } : {}),
                  }}
                >
                  {t.label}
                </button>
              </li>
            ))}
          </ul>
        )}

        {/* Expected Returns inner sub-tab */}
        {activeTab === 'scenarios' && scenarioInnerTab === 'returns' && selectedScenarioId && (
          <>
            <p className="text-muted mb-2" style={{ fontSize: '0.8rem' }}>
              Forward-looking expected returns and volatilities for <strong>{selectedScenario?.name || ''}</strong>.
            </p>
            {selectedScenarioId && (
              <div className="d-flex gap-2 mb-2">
                <button
                  className="btn btn-outline-primary btn-sm"
                  onClick={() => { setShowReturnsPaste(true); setReturnsPasteText(''); setReturnsPasteError(null); }}
                  title="Bulk-update returns by pasting three columns (SAC, Annual Return %, Annual Vol %) from a spreadsheet. Decimals or percentages — auto-detected."
                >
                  <i className="bi bi-clipboard me-1"></i>Paste from Excel
                </button>
                {scenarioReturns.length > 0 && (
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={downloadReturnsTemplate}
                    title="Download an Excel workbook containing ONLY this scenario's expected returns (metadata + expected_returns sheets, no covariance). Compatible with Upload Scenario when you only want to update returns."
                  >
                    <i className="bi bi-download me-1"></i>Download Returns Template
                  </button>
                )}
              </div>
            )}
            {showReturnsPaste && (
              <div className="card mb-3" style={{ border: '1px solid var(--input-border)' }}>
                <div className="card-body py-2 px-3">
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <strong style={{ fontSize: '0.82rem' }}>Paste Expected Returns</strong>
                    <button className="btn-close btn-close-sm" onClick={() => setShowReturnsPaste(false)} style={{ fontSize: '0.6rem' }} />
                  </div>
                  <p className="text-muted mb-1" style={{ fontSize: '0.75rem' }}>
                    Copy 3 columns from Excel: <strong>Sub Asset Class | Return (%) | Volatility (%)</strong>. Include the header row. Values can be decimals (0.05) or percentages (5.0) — auto-detected.
                  </p>
                  <textarea
                    className="form-control form-control-sm mb-2"
                    rows={6}
                    style={{ fontSize: '0.75rem', fontFamily: 'monospace' }}
                    placeholder="Sub Asset Class&#9;Annual Return (%)&#9;Annual Volatility (%)&#10;Australian Equities&#9;7.50&#9;15.00&#10;International Equities&#9;8.00&#9;16.50"
                    value={returnsPasteText}
                    onChange={e => setReturnsPasteText(e.target.value)}
                  />
                  {returnsPasteError && <div className="text-danger mb-1" style={{ fontSize: '0.75rem' }}>{returnsPasteError}</div>}
                  <button className="btn btn-primary btn-sm" onClick={handlePasteReturns} disabled={!returnsPasteText.trim()}>
                    Apply
                  </button>
                </div>
              </div>
            )}
            {selectedScenarioId && scenarioReturns.length > 0 && (
              <div className="table-responsive">
                <table className="table mb-0">
                  <thead>
                    <tr>
                      <th title="Sub-asset class code as defined in the Sub Asset Classes tab">Sub Asset Class</th>
                      <th title="Forward-looking expected annual return (geometric or arithmetic — match your downstream consumer's convention). Feeds the optimiser, portfolio reports, and retirement Monte Carlo.">Annual Return (%)</th>
                      <th title="Forward-looking expected annual volatility (standard deviation of annual returns). This becomes the diagonal of the covariance matrix exactly — D · ρ · D — when the matrix is computed or saved.">Annual Volatility (%)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scenarioReturns.map((r, idx) => (
                      <tr key={r.sub_asset_class}>
                        <td className="align-middle fw-medium">{r.sub_asset_class}</td>
                        <td>
                          <div className="input-group input-group-sm">
                            <input
                              type="number"
                              className="form-control form-control-sm"
                              value={(r.annual_return * 100).toFixed(2)}
                              step="0.01"
                              onChange={e => handleReturnChange(idx, 'annual_return', parseFloat(e.target.value) / 100)}
                              title="Expected annual return in percent (e.g. 7.5 for 7.5% p.a.). Stored internally as a decimal."
                            />
                            <span className="input-group-text">%</span>
                          </div>
                        </td>
                        <td>
                          <div className="input-group input-group-sm">
                            <input
                              type="number"
                              className="form-control form-control-sm"
                              value={(r.annual_volatility * 100).toFixed(2)}
                              step="0.01"
                              onChange={e => handleReturnChange(idx, 'annual_volatility', parseFloat(e.target.value) / 100)}
                              title="Annual volatility in percent (e.g. 15.0 for 15% p.a.). Changing this triggers automatic recompute of every covariance cell involving this SAC."
                            />
                            <span className="input-group-text">%</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {selectedScenarioId && scenarioReturns.length === 0 && !showReturnsPaste && (
              <div className="text-muted text-center py-4">
                No expected returns data for this scenario. Import from Blender DB, paste from Excel, or add returns manually.
              </div>
            )}
            {!selectedScenarioId && (
              <div className="text-muted text-center py-4">
                Select a scenario above, or import from Blender DB to get started.
              </div>
            )}
          </>
        )}

        {/* Covariance Matrix Tab */}
        {activeTab === 'scenarios' && scenarioInnerTab === 'covariance' && selectedScenarioId && (
          <>
            <p className="text-muted mb-2" style={{ fontSize: '0.8rem' }}>
              Edit correlations below for <strong>{selectedScenario?.name || ''}</strong> — covariance is auto-computed from correlation x vol_i x vol_j.
            </p>
            {selectedScenarioId && covSacList.length > 0 && (
              <>
                <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
                  <div className="btn-group btn-group-sm" title="Switch the grid display between correlation values (−1 to +1) and covariance (shown as basis points × 10,000 for readability)">
                    <button
                      className={`btn ${covViewMode === 'correlation' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => setCovViewMode('correlation')}
                      title="Show correlation values (−1 to +1). Edits to lower-triangle cells update the underlying correlation and recompute covariance as corr × vol_i × vol_j."
                    >
                      Correlation
                    </button>
                    <button
                      className={`btn ${covViewMode === 'covariance' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => setCovViewMode('covariance')}
                      title="Show annualised covariance values (multiplied by 10,000 for readability — i.e. basis points)"
                    >
                      Covariance
                    </button>
                  </div>
                  <button
                    className="btn btn-outline-primary btn-sm"
                    onClick={() => { setShowCovPaste(true); setCovPasteText(''); setCovPasteError(null); }}
                    title="Paste a full N×N matrix from Excel (asset class names in first row and first column). Auto-detects correlation vs covariance by checking whether the diagonal is ~1."
                  >
                    <i className="bi bi-clipboard me-1"></i>Paste from Excel
                  </button>
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={downloadCovTemplate}
                    title="Download an Excel workbook containing ONLY this scenario's covariance matrix (metadata + covariance sheets, no expected returns). Long-form pair list compatible with Upload Scenario."
                  >
                    <i className="bi bi-download me-1"></i>Download Matrix Template
                  </button>
                  <button
                    className={`btn btn-sm ${showCompPanel ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={() => setShowCompPanel(v => !v)}
                    title="Open the compute panel — build the covariance from historical index data using Stambaugh backfill, Ledoit-Wolf shrinkage, regime blending, and optional pair tilts"
                  >
                    <i className="bi bi-calculator me-1"></i>
                    {showCompPanel ? 'Hide Compute Panel' : 'Compute from History'}
                  </button>
                  {compPreview && (
                    <span
                      className="badge bg-info text-dark"
                      style={{ fontSize: '0.7rem' }}
                      title="The matrix grid below is showing the previewed computed matrix (blue-tinted cells). Nothing has been persisted yet — use Save or Discard inside the compute panel."
                    >
                      Previewing computed matrix — click Save or Discard
                    </span>
                  )}
                </div>

                {showCompPanel && (
                  <div className="card mb-3" style={{ border: '1px solid var(--input-border)' }}>
                    <div className="card-body py-2 px-3">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <strong style={{ fontSize: '0.85rem' }}>Compute Covariance from Historical Data</strong>
                        <button className="btn-close btn-close-sm" onClick={() => setShowCompPanel(false)} style={{ fontSize: '0.6rem' }} />
                      </div>
                      <p className="text-muted mb-2" style={{ fontSize: '0.75rem' }}>
                        Pulls monthly index history from <code>monthly_historic_returns</code>, resamples to the chosen period-end levels, then applies optional Stambaugh backfill, Ledoit-Wolf shrinkage, regime-blending, and your tilts. Always finishes with a PSD repair. The final Σ is annualised using each scenario's annual vols. Diagnostics report what happened.
                      </p>

                      {/* Calculation frequency */}
                      <div className="row g-2 mb-2">
                        <div className="col-md-3">
                          <label
                            className="form-label small"
                            title="Frequency at which returns are computed from the monthly price-level history. Quarterly is the SAA default — monthly retains more obs but captures short-term noise (vol clustering, microstructure); annual best matches a 10y horizon but has very few obs and is only sensible with 30+ years of data."
                          >
                            Return frequency
                          </label>
                          <select
                            className="form-select form-select-sm"
                            value={compFrequency}
                            onChange={e => setCompFrequency(e.target.value)}
                            title="Return frequency used for the correlation estimate"
                          >
                            <option value="monthly">Monthly</option>
                            <option value="quarterly">Quarterly (default)</option>
                            <option value="annual">Annual</option>
                          </select>
                        </div>
                        <div className="col-md-9 d-flex align-items-end">
                          <small className="text-muted" style={{ fontSize: '0.72rem' }}>
                            Thresholds scale automatically: 5-year minimum overlap, 2-year minimum for Ledoit-Wolf. The output Σ is always annualised.
                          </small>
                        </div>
                      </div>

                      {/* Sample window + backfill */}
                      <div className="row g-2 mb-2">
                        <div className="col-md-3">
                          <label
                            className="form-label small"
                            title="Quick-pick a historical sample window. 'Custom' lets you set the Start/End dates manually."
                          >
                            Sample window
                          </label>
                          <select
                            className="form-select form-select-sm"
                            value={compHistoryPreset}
                            onChange={e => {
                              const key = e.target.value;
                              setCompHistoryPreset(key);
                              const preset = HISTORY_PRESETS.find(p => p.key === key);
                              if (preset && preset.start) {
                                setCompHistoryStart(preset.start());
                                setCompHistoryEnd(preset.end());
                              }
                            }}
                          >
                            {HISTORY_PRESETS.map(p => (
                              <option key={p.key} value={p.key}>{p.label}</option>
                            ))}
                          </select>
                        </div>
                        <div className="col-md-3">
                          <label className="form-label small" title="Earliest date in the historical sample. Shortening the window emphasises more recent regimes; lengthening it adds more data but may average over irrelevant macro regimes.">History Start</label>
                          <input
                            type="date"
                            className="form-control form-control-sm"
                            value={compHistoryStart}
                            onChange={e => { setCompHistoryStart(e.target.value); setCompHistoryPreset('custom'); }}
                            title="Earliest month to include in the historical sample"
                          />
                        </div>
                        <div className="col-md-3">
                          <label className="form-label small" title="Latest date to include. Blank = use everything up to today.">History End (blank = today)</label>
                          <input
                            type="date"
                            className="form-control form-control-sm"
                            value={compHistoryEnd}
                            onChange={e => { setCompHistoryEnd(e.target.value); setCompHistoryPreset('custom'); }}
                            title="Latest month to include. Leave blank to use up to today."
                          />
                        </div>
                        <div className="col-md-3 d-flex align-items-end">
                          <div
                            className="form-check form-switch"
                            title="When ON, short-history SACs (e.g. PE, EM) are extended backwards using OLS regression on the SAC's configured backfill proxies. The proxies are defined per-SAC in the Sub Asset Classes tab. Minimum 5-year overlap required (scales with the chosen frequency)."
                          >
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id="backfillToggle"
                              checked={compBackfill}
                              onChange={e => setCompBackfill(e.target.checked)}
                            />
                            <label className="form-check-label small" htmlFor="backfillToggle">
                              Stambaugh backfill
                            </label>
                          </div>
                        </div>
                      </div>

                      {/* Regime weights */}
                      <div className="mb-2">
                        <label
                          className="form-label small fw-bold"
                          title="Blend correlations from different macro regimes. The pipeline filters historical months to each regime's date window, computes a correlation matrix per regime, then takes a weighted average."
                        >
                          Regime Blending
                        </label>
                        {compRegimes.length === 0 ? (
                          <div className="text-muted small">
                            No macro regimes configured. Add some in Investment Config &rarr; Macro Regimes to enable blending.
                          </div>
                        ) : (
                          <div className="row g-2">
                            {compRegimes.map(r => (
                              <div key={r.id} className="col-md-4">
                                <label
                                  className="form-label small"
                                  title={r.description || `Regime window: ${r.start_date} to ${r.end_date}`}
                                >
                                  {r.regime_name}
                                  <span className="text-muted ms-1" style={{ fontSize: '0.7rem' }}>
                                    ({r.start_date} &ndash; {r.end_date})
                                  </span>
                                </label>
                                <input
                                  type="number"
                                  className="form-control form-control-sm"
                                  step="0.05"
                                  min="0"
                                  placeholder="0 = excluded"
                                  value={compRegimeWeights[r.regime_name] ?? ''}
                                  onChange={e => setCompRegimeWeights(prev => ({
                                    ...prev,
                                    [r.regime_name]: e.target.value,
                                  }))}
                                  title={`Relative weight to apply to the ${r.regime_name} regime when blending. Weights are renormalised so the surviving non-zero values sum to 1. Regimes with fewer than 60 in-sample months are dropped automatically.`}
                                />
                              </div>
                            ))}
                          </div>
                        )}
                        <small className="text-muted">Weights are renormalised across surviving regimes. Leave all blank for full-history correlation.</small>
                      </div>

                      {/* Tilts */}
                      <div className="mb-2">
                        <label
                          className="form-label small fw-bold d-flex justify-content-between align-items-center"
                          title="Multiplicative overrides applied to specific correlation pairs after the regime blend. Useful for sharper, surgical views (e.g. 'in this scenario I think equity-bond should be 2× the historical correlation'). Applied as ρ_new = clip(ρ_blend × multiplier, −0.99, +0.99)."
                        >
                          <span>Pair Tilts (multiply specific correlations)</span>
                          <button
                            className="btn btn-outline-primary btn-sm"
                            onClick={handleAddTilt}
                            title="Add a new tilt row. Pick two SACs and a multiplier."
                          >
                            + Add Tilt
                          </button>
                        </label>
                        {compTilts.length === 0 ? (
                          <div className="text-muted small">No tilts. Add a tilt to bias a specific correlation pair (e.g. EQ_AU &times; BND_AU by 2.0 in a high-inflation scenario).</div>
                        ) : (
                          compTilts.map((t, idx) => (
                            <div key={idx} className="row g-2 mb-1 align-items-end">
                              <div className="col-md-3">
                                <select
                                  className="form-select form-select-sm"
                                  value={t.a}
                                  onChange={e => handleUpdateTilt(idx, 'a', e.target.value)}
                                  title="First asset in the pair (order doesn't matter)"
                                >
                                  <option value="">-- Asset 1 --</option>
                                  {covSacList.map(s => (
                                    <option key={s} value={s}>{s}</option>
                                  ))}
                                </select>
                              </div>
                              <div className="col-md-3">
                                <select
                                  className="form-select form-select-sm"
                                  value={t.b}
                                  onChange={e => handleUpdateTilt(idx, 'b', e.target.value)}
                                  title="Second asset in the pair (order doesn't matter)"
                                >
                                  <option value="">-- Asset 2 --</option>
                                  {covSacList.map(s => (
                                    <option key={s} value={s}>{s}</option>
                                  ))}
                                </select>
                              </div>
                              <div className="col-md-2">
                                <input
                                  type="number"
                                  step="0.1"
                                  className="form-control form-control-sm"
                                  placeholder="multiplier"
                                  value={t.multiplier}
                                  onChange={e => handleUpdateTilt(idx, 'multiplier', e.target.value)}
                                  title="Multiplier applied to the post-blend correlation. 1.0 = no change. 2.0 = double the magnitude. Result is clipped to ±0.99."
                                />
                              </div>
                              <div className="col-md-1">
                                <button
                                  className="btn btn-outline-danger btn-sm"
                                  onClick={() => handleRemoveTilt(idx)}
                                  title="Remove this tilt"
                                >
                                  &times;
                                </button>
                              </div>
                            </div>
                          ))
                        )}
                      </div>

                      {compError && (
                        <div className="alert alert-danger py-2 mb-2" style={{ fontSize: '0.8rem' }}>{compError}</div>
                      )}

                      <div className="d-flex gap-2">
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={handleComputePreview}
                          disabled={compLoading || !selectedScenarioId}
                          title="Run the compute pipeline with the current settings and overlay the resulting matrix on the grid in light blue. Nothing is persisted — inspect the diagnostics, then use the Save button above to commit it (or Discard)."
                        >
                          {compLoading ? 'Computing...' : 'Preview from History'}
                        </button>
                        {compPreview && (
                          <button
                            className="btn btn-outline-secondary btn-sm"
                            onClick={handleComputeDiscard}
                            title="Clear the previewed overlay and revert the matrix grid to the persisted scenario values"
                          >
                            Discard Preview
                          </button>
                        )}
                      </div>

                      {/* Diagnostics */}
                      {compPreview?.diagnostics && (
                        <div className="mt-3 small">
                          <strong>Diagnostics</strong>
                          <div className="row g-2 mt-1">
                            <div className="col-md-6">
                              <div className="text-muted">PSD repair</div>
                              <div>
                                Min eigenvalue before: <code>{(compPreview.diagnostics.psd_repair?.min_eigenvalue_before ?? 0).toExponential(2)}</code><br />
                                Min eigenvalue after: <code>{(compPreview.diagnostics.psd_repair?.min_eigenvalue_after ?? 0).toExponential(2)}</code><br />
                                Frobenius distance moved: <code>{(compPreview.diagnostics.psd_repair?.frobenius_distance ?? 0).toFixed(4)}</code>
                                {(compPreview.diagnostics.psd_repair?.frobenius_distance ?? 0) > 0.1 && (
                                  <span className="badge bg-warning text-dark ms-2">large repair</span>
                                )}
                              </div>
                            </div>
                            <div className="col-md-6">
                              <div className="text-muted">Ledoit-Wolf</div>
                              <div>
                                Shrinkage intensity: <code>{(compPreview.diagnostics.lw_shrinkage_intensity ?? 0).toFixed(3)}</code><br />
                                Rectangular {compPreview.diagnostics.frequency || 'periods'}: <code>{compPreview.diagnostics.lw_rectangular_periods ?? 0}</code><br />
                                Panel rows ({compPreview.diagnostics.frequency || 'periods'}): <code>{compPreview.diagnostics.panel_rows ?? 0}</code>
                                {compPreview.diagnostics.base_correlation?.pairs_from_longer_pairwise_history > 0 && (
                                  <><br />Pairs using longer pairwise history: <code>{compPreview.diagnostics.base_correlation.pairs_from_longer_pairwise_history}</code></>
                                )}
                              </div>
                            </div>
                          </div>

                          {compPreview.diagnostics.per_sac_first_valid_date && (
                            <div className="mt-2">
                              <div className="text-muted">First valid date per SAC</div>
                              <div style={{ fontSize: '0.72rem', fontFamily: 'monospace' }}>
                                {Object.entries(compPreview.diagnostics.per_sac_first_valid_date)
                                  .map(([sac, dt]) => `${sac}: ${dt || 'none'}`)
                                  .join('   |   ')}
                              </div>
                            </div>
                          )}

                          {compPreview.diagnostics.tilts_realised && Object.keys(compPreview.diagnostics.tilts_realised).length > 0 && (
                            <div className="mt-2">
                              <div className="text-muted">Tilt outcomes (requested vs post-repair)</div>
                              <table className="table table-sm mt-1" style={{ fontSize: '0.75rem' }}>
                                <thead>
                                  <tr><th>Pair</th><th>Base ρ</th><th>×</th><th>Requested</th><th>Post-repair</th><th>Δ from repair</th></tr>
                                </thead>
                                <tbody>
                                  {Object.entries(compPreview.diagnostics.tilts_realised).map(([k, v]) => (
                                    <tr key={k}>
                                      <td>{k}</td>
                                      <td>{(v.base ?? 0).toFixed(3)}</td>
                                      <td>{(v.multiplier ?? 0).toFixed(2)}</td>
                                      <td>{(v.requested ?? 0).toFixed(3)}</td>
                                      <td>{(v.applied_post_repair ?? 0).toFixed(3)}</td>
                                      <td>
                                        <code>{(v.psd_redistribution ?? 0).toFixed(3)}</code>
                                        {Math.abs(v.psd_redistribution ?? 0) > 0.05 && (
                                          <span className="text-warning ms-1">⚠</span>
                                        )}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}

                          {compPreview.diagnostics.regime_blend && typeof compPreview.diagnostics.regime_blend === 'object' && Object.keys(compPreview.diagnostics.regime_blend).length > 0 && (
                            <div className="mt-2">
                              <div className="text-muted">Regime blend</div>
                              <div style={{ fontSize: '0.72rem', fontFamily: 'monospace' }}>
                                {Object.entries(compPreview.diagnostics.regime_blend).map(([k, v]) => (
                                  <div key={k}>{k}: {typeof v === 'object' ? JSON.stringify(v) : String(v)}</div>
                                ))}
                              </div>
                            </div>
                          )}

                          {compPreview.diagnostics.backfill && typeof compPreview.diagnostics.backfill === 'object' && Object.keys(compPreview.diagnostics.backfill).length > 0 && (
                            <div className="mt-2">
                              <div className="text-muted">Backfill</div>
                              <div style={{ fontSize: '0.72rem', fontFamily: 'monospace' }}>
                                {Object.entries(compPreview.diagnostics.backfill).map(([k, v]) => (
                                  <div key={k}>{k}: {typeof v === 'object' ? JSON.stringify(v) : String(v)}</div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {showCovPaste && (
                  <div className="card mb-3" style={{ border: '1px solid var(--input-border)' }}>
                    <div className="card-body py-2 px-3">
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <strong style={{ fontSize: '0.82rem' }}>Paste {covViewMode === 'correlation' ? 'Correlation' : 'Covariance'} Matrix</strong>
                        <button className="btn-close btn-close-sm" onClick={() => setShowCovPaste(false)} style={{ fontSize: '0.6rem' }} />
                      </div>
                      <p className="text-muted mb-1" style={{ fontSize: '0.75rem' }}>
                        Copy a full NxN matrix from Excel with asset class names in the first row and first column. Auto-detects whether values are correlation (diagonal = 1) or covariance.
                      </p>
                      <textarea
                        className="form-control form-control-sm mb-2"
                        rows={8}
                        style={{ fontSize: '0.72rem', fontFamily: 'monospace' }}
                        placeholder={"&#9;Aus Equity&#9;Intl Equity&#9;Fixed Inc\nAus Equity&#9;1.00&#9;0.75&#9;0.10\nIntl Equity&#9;0.75&#9;1.00&#9;0.15\nFixed Inc&#9;0.10&#9;0.15&#9;1.00"}
                        value={covPasteText}
                        onChange={e => setCovPasteText(e.target.value)}
                      />
                      {covPasteError && <div className="text-danger mb-1" style={{ fontSize: '0.75rem' }}>{covPasteError}</div>}
                      <button className="btn btn-primary btn-sm" onClick={handlePasteCov} disabled={!covPasteText.trim()}>
                        Apply
                      </button>
                    </div>
                  </div>
                )}
                <div style={{ width: '100%', maxHeight: '70vh', overflowX: 'hidden', overflowY: 'auto' }}>
                  <table
                    className="table mb-0"
                    style={{ width: '100%', tableLayout: 'fixed' }}
                  >
                    <colgroup>
                      {/* First column = row labels, fixed narrow. Remaining N columns share the rest equally. */}
                      <col style={{ width: '70px' }} />
                      {covSacList.map(sac => (
                        <col key={sac} style={{ width: `calc((100% - 70px) / ${covSacList.length})` }} />
                      ))}
                    </colgroup>
                    <thead>
                      <tr>
                        <th style={{ background: '#072040' }}></th>
                        {covSacList.map(sac => (
                          <th
                            key={sac}
                            style={{
                              writingMode: 'vertical-rl',
                              textOrientation: 'mixed',
                              padding: '4px 2px',
                              fontSize: '0.65rem',
                              textAlign: 'center',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {sac}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {covSacList.map((rowSac) => (
                        <tr key={rowSac}>
                          <td style={{ fontWeight: 600, fontSize: '0.68rem', background: '#f8f9fa', whiteSpace: 'nowrap', color: '#072040', padding: '2px 6px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{rowSac}</td>
                          {covSacList.map((colSac) => {
                            const isDiagonal = rowSac === colSac;
                            const isLowerTriangle = covSacList.indexOf(rowSac) > covSacList.indexOf(colSac);
                            const val = getCovValue(rowSac, colSac, covViewMode);
                            const corrVal = getCovValue(rowSac, colSac, 'correlation');
                            const isPreview = compPreview && !isDiagonal && getPreviewValue(rowSac, colSac, covViewMode) !== null;

                            // Colour scale for correlation: green for positive, red for negative
                            let bgColor = '#fff';
                            if (!isDiagonal && covViewMode === 'correlation') {
                              const abs = Math.min(Math.abs(corrVal), 1);
                              if (corrVal > 0) bgColor = `rgba(145,207,155,${abs * 0.6})`;
                              else if (corrVal < 0) bgColor = `rgba(255,193,7,${abs * 0.6})`;
                            }
                            // Preview overlay tint (light blue) takes precedence
                            if (isPreview) {
                              bgColor = 'rgba(89,155,227,0.25)';
                            }

                            if (isDiagonal) {
                              return (
                                <td
                                  key={colSac}
                                  style={{ background: '#e9ecef', textAlign: 'center', fontWeight: 600, fontSize: '0.68rem', padding: '2px 3px' }}
                                  title={covViewMode === 'correlation'
                                    ? `${rowSac}: diagonal = 1.00 by definition`
                                    : `${rowSac}: diagonal = vol² = (${rowSac} annual vol)². Edit via the Expected Returns tab.`}
                                >
                                  {covViewMode === 'correlation' ? '1.00' : (val * 10000).toFixed(2)}
                                </td>
                              );
                            }

                            if (isLowerTriangle) {
                              return (
                                <td
                                  key={colSac}
                                  style={{ background: bgColor, textAlign: 'center', padding: '1px' }}
                                  title={`${rowSac} ↔ ${colSac}: ${covViewMode}. Editable. Upper-triangle mirror updates automatically. ${isPreview ? '(Showing computed preview — light blue tint)' : ''}`}
                                >
                                  <input
                                    type="number"
                                    step="0.01"
                                    className="form-control form-control-sm"
                                    style={{ width: '100%', minWidth: 0, fontSize: '0.66rem', padding: '1px 2px', textAlign: 'center', border: '1px solid #dee2e6', boxSizing: 'border-box' }}
                                    value={covViewMode === 'correlation' ? val.toFixed(2) : (val * 10000).toFixed(2)}
                                    onChange={e => {
                                      const raw = parseFloat(e.target.value) || 0;
                                      const actual = covViewMode === 'covariance' ? raw / 10000 : raw;
                                      setCovValue(rowSac, colSac, covViewMode, actual);
                                    }}
                                    title={`Edit ${rowSac} ↔ ${colSac} ${covViewMode}. ${covViewMode === 'correlation' ? 'Range −1 to +1.' : 'Shown in basis points (×10,000).'}`}
                                  />
                                </td>
                              );
                            }

                            // Upper triangle — read-only mirror
                            return (
                              <td
                                key={colSac}
                                style={{ background: bgColor, textAlign: 'center', color: '#6c757d', fontSize: '0.68rem', padding: '2px 3px' }}
                                title={`${rowSac} ↔ ${colSac}: ${covViewMode} (read-only mirror of the lower-triangle cell)`}
                              >
                                {covViewMode === 'correlation' ? val.toFixed(2) : (val * 10000).toFixed(2)}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {!covPsdCheck.valid && (
                  <div className="alert alert-warning mt-2 mb-2 py-2 d-flex align-items-center justify-content-between" style={{ fontSize: '0.85rem' }}>
                    <span><strong>Warning:</strong> {covPsdCheck.message}. The matrix cannot be saved until this is corrected.</span>
                    <button className="btn btn-sm btn-outline-dark ms-3" onClick={handleFixPsd}>
                      Convert to Nearest Positive Definite
                    </button>
                  </div>
                )}
                <small className="text-muted">
                  {covViewMode === 'covariance'
                    ? 'Values shown as basis points (x10,000). Covariance = correlation x vol_i x vol_j. Changing volatility in Expected Returns will auto-update covariance values.'
                    : 'Edit the lower triangle; upper triangle mirrors automatically. Covariance is recomputed from correlation x vol_i x vol_j.'}
                </small>
              </>
            )}
            {selectedScenarioId && covSacList.length === 0 && (
              <div className="text-muted text-center py-4">
                No covariance data. Ensure expected returns are populated first (they define the SAC list).
              </div>
            )}
            {!selectedScenarioId && (
              <div className="text-muted text-center py-4">
                Select a scenario above to view or edit the covariance matrix.
              </div>
            )}
          </>
        )}

        {/* Asset Class Data Tab */}
        {activeTab === 'assetClassData' && (() => {
          const NAVY = '#072040';
          const GREY = '#E7E6E6';
          const pct1 = (x) => `${(x * 100).toFixed(1)}%`;
          const defaultScenario = scenarios.find(s => s.is_default);
          const cols = acdSelectedRows;

          // Cell style helpers keep the four data rows consistent.
          const labelCell = (bg) => ({
            fontWeight: 700, color: '#212529', padding: '4px 10px',
            whiteSpace: 'nowrap', background: bg, fontSize: '0.8rem',
          });
          const dataCell = (bg) => ({
            padding: '4px 10px', background: bg, fontSize: '0.8rem',
            color: '#212529', whiteSpace: 'nowrap',
          });
          const spacerRow = (bg) => (
            <tr><td colSpan={cols.length + 1} style={{ height: '10px', background: bg, padding: 0 }} /></tr>
          );

          return (
            <>
              <p className="text-muted mb-2" style={{ fontSize: '0.8rem' }}>
                Presentation table drawn from the default CMA scenario
                {defaultScenario ? <> (<strong>{defaultScenario.name}</strong>)</> : ' (none set — mark a scenario as default on the Scenarios tab)'}.
                Income comes from the Sub Asset Classes tab; growth is the residual (10yr return − income); standard deviation is the scenario volatility.
                Classes ticked 'Income = Return' on the Sub Asset Classes tab show income equal to the return and zero growth.
                Select the sub-asset classes to include and edit the long-run macro assumptions, then Export PNG.
              </p>

              {/* Selection control bar — not part of the exported image */}
              {acdRows.length > 0 && (
                <div className="mb-3 p-2" style={{ border: '1px solid var(--input-border)', borderRadius: '4px' }}>
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <strong style={{ fontSize: '0.8rem' }}>Include sub-asset classes ({cols.length}/{acdRows.length})</strong>
                    <div className="d-flex gap-2">
                      <button className="btn btn-outline-secondary btn-sm" onClick={() => setAcdDeselected(new Set())}>Select all</button>
                      <button className="btn btn-outline-secondary btn-sm" onClick={() => setAcdDeselected(new Set(acdRows.map(r => r.code)))}>Deselect all</button>
                    </div>
                  </div>
                  <div className="d-flex flex-wrap gap-2">
                    {acdRows.map(r => (
                      <label key={r.code} className="d-flex align-items-center gap-1" style={{ fontSize: '0.78rem', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          className="form-check-input mt-0"
                          checked={!acdDeselected.has(r.code)}
                          onChange={() => toggleAcdSac(r.code)}
                        />
                        {r.label}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {acdLoading && (
                <div className="text-muted text-center py-4">
                  <div className="spinner-border spinner-border-sm me-2" role="status" />
                  Loading default scenario returns…
                </div>
              )}

              {!acdLoading && !defaultScenarioId && (
                <div className="text-muted text-center py-4">
                  No default CMA scenario. Set one as default on the Scenarios tab to populate this table.
                </div>
              )}

              {!acdLoading && defaultScenarioId && acdRows.length === 0 && (
                <div className="text-muted text-center py-4">
                  The default scenario has no expected returns matching the taxonomy. Populate returns on the Scenarios tab.
                </div>
              )}

              {!acdLoading && cols.length > 0 && (
                <div style={{ overflowX: 'auto' }}>
                  {/* Everything inside this ref is what gets captured to PNG.
                      inline-block so the box hugs the (widest) main table rather
                      than the scroll container's width. */}
                  <div ref={acdExportRef} style={{ background: '#fff', display: 'inline-block' }}>
                    {/* Title bar */}
                    <div style={{ background: NAVY, color: '#fff', fontWeight: 700, textAlign: 'center', padding: '8px 12px', fontSize: '1rem' }}>
                      Asset Class Data
                    </div>

                    {/* Main data table */}
                    <table style={{ borderCollapse: 'collapse', width: 'auto', tableLayout: 'auto' }}>
                      <thead>
                        <tr>
                          <th style={{ background: NAVY, minWidth: '190px' }} />
                          {cols.map(c => (
                            <th
                              key={c.code}
                              style={{
                                background: NAVY, color: '#fff', fontWeight: 700, verticalAlign: 'bottom',
                                textAlign: 'left', padding: '6px 10px', fontSize: '0.78rem',
                                minWidth: '78px',
                              }}
                            >
                              {c.label}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td style={labelCell('#fff')}>10 Year Annualised Return</td>
                          {cols.map(c => <td key={c.code} style={dataCell('#fff')}>{pct1(c.totalReturn)}</td>)}
                        </tr>
                        {spacerRow('#fff')}
                        <tr>
                          <td style={labelCell(GREY)}>Income</td>
                          {cols.map(c => <td key={c.code} style={dataCell(GREY)}>{pct1(c.income)}</td>)}
                        </tr>
                        <tr>
                          <td style={labelCell(GREY)}>Growth</td>
                          {cols.map(c => <td key={c.code} style={dataCell(GREY)}>{pct1(c.growth)}</td>)}
                        </tr>
                        {spacerRow('#fff')}
                        <tr>
                          <td style={labelCell(GREY)}>Standard Deviation</td>
                          {cols.map(c => <td key={c.code} style={dataCell(GREY)}>{pct1(c.vol)}</td>)}
                        </tr>
                      </tbody>
                    </table>

                    {/* Long Run Macro Assumptions — fixed narrow width, left-aligned
                        under the main table (matches the house layout). */}
                    <table style={{ borderCollapse: 'collapse', marginTop: '18px', width: '300px', tableLayout: 'fixed' }}>
                      <colgroup>
                        <col style={{ width: '200px' }} />
                        <col style={{ width: '100px' }} />
                      </colgroup>
                      <thead>
                        <tr>
                          <th colSpan={2} style={{ background: NAVY, color: '#fff', fontWeight: 700, textAlign: 'center', padding: '5px 10px', fontSize: '0.82rem' }}>
                            Long Run Macro Assumptions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          { key: 'cpi', label: 'CPI', decimals: 1, step: 0.1 },
                          { key: 'awote', label: 'AWOTE', decimals: 1, step: 0.1 },
                          { key: 'franking', label: 'AU Equity Franking %', decimals: 0, step: 1 },
                        ].map((m, i) => (
                          <tr key={m.key}>
                            <td style={{ fontWeight: 700, color: '#212529', padding: '3px 10px', background: i % 2 ? '#fff' : GREY, fontSize: '0.8rem' }}>
                              {m.label}
                            </td>
                            <td style={{ padding: '3px 6px', background: i % 2 ? '#fff' : GREY, textAlign: 'right', fontSize: '0.8rem', width: '90px' }}>
                              <MacroCellInput
                                value={acdMacro[m.key]}
                                decimals={m.decimals}
                                step={m.step}
                                onChange={v => setAcdMacro(prev => ({ ...prev, [m.key]: v }))}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          );
        })()}

        {/* Create Scenario Modal */}
        {showCreateModal && (
          <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">New CMA Scenario</h5>
                  <button type="button" className="btn-close" onClick={() => setShowCreateModal(false)} />
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Scenario Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={newScenarioName}
                      onChange={e => setNewScenarioName(e.target.value)}
                      placeholder="e.g. Bear Market 2026"
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Description (optional)</label>
                    <textarea
                      className="form-control"
                      value={newScenarioDesc}
                      onChange={e => setNewScenarioDesc(e.target.value)}
                      rows={2}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Clone from existing scenario (optional)</label>
                    <select
                      className="form-select"
                      value={cloneFromId || ''}
                      onChange={e => setCloneFromId(parseInt(e.target.value) || null)}
                    >
                      <option value="">-- Start empty --</option>
                      {scenarios.map(s => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={() => setShowCreateModal(false)}>Cancel</button>
                  <button className="btn btn-primary" onClick={handleCreateScenario} disabled={saving || !newScenarioName.trim()}>
                    {saving ? 'Creating...' : 'Create'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Unlock modal — reason is mandatory and permanently recorded */}
        {unlockTarget && (
          <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    <i className="bi bi-unlock me-2"></i>Unlock "{unlockTarget.name}"
                  </h5>
                  <button type="button" className="btn-close" onClick={() => setUnlockTarget(null)} />
                </div>
                <div className="modal-body">
                  <div className="alert alert-warning py-2 small">
                    This scenario was finalised
                    {unlockTarget.locked_at ? ` on ${new Date(unlockTarget.locked_at).toLocaleDateString('en-AU')}` : ''}.
                    Unlocking makes it editable again — the numbers will no longer be guaranteed
                    to match what was signed off. Your reason is recorded permanently.
                  </div>
                  <label className="form-label">Reason for unlocking <span className="text-danger">*</span></label>
                  <textarea
                    className="form-control"
                    rows={3}
                    value={unlockReason}
                    onChange={(e) => setUnlockReason(e.target.value)}
                    placeholder="e.g. Adding the new Private Credit sub asset class approved at the July IC meeting"
                    autoFocus
                  />
                  {unlockReason.trim().length > 0 && unlockReason.trim().length < 5 && (
                    <div className="form-text text-danger">Reason must be at least 5 characters.</div>
                  )}
                  <div className="mt-3">
                    <div className="small fw-semibold mb-1">Lock history</div>
                    {unlockHistory === null ? (
                      <div className="text-muted small">Loading…</div>
                    ) : unlockHistory.length === 0 ? (
                      <div className="text-muted small">No recorded events (locked before history was introduced).</div>
                    ) : (
                      <ul className="list-unstyled small mb-0" style={{ maxHeight: 160, overflowY: 'auto' }}>
                        {unlockHistory.map((e, i) => (
                          <li key={i} className="mb-1">
                            <i className={`bi ${e.action === 'locked' ? 'bi-lock-fill' : 'bi-unlock'} me-1`} />
                            <strong>{e.action === 'locked' ? 'Locked' : 'Unlocked'}</strong>
                            {e.created_at ? ` ${new Date(e.created_at).toLocaleString('en-AU')}` : ''}
                            {e.user_email ? ` by ${e.user_email}` : ''}
                            {e.reason ? <span className="text-muted"> — {e.reason}</span> : null}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={() => setUnlockTarget(null)}>Cancel</button>
                  <button
                    className="btn btn-dark"
                    onClick={handleUnlockScenario}
                    disabled={unlocking || unlockReason.trim().length < 5}
                  >
                    {unlocking ? 'Unlocking…' : 'Unlock scenario'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
    </>
  );
};

export default CapitalMarketAssumptions;
